// Raft Deck client (phase 1, placeholder art): the page around the map. The map itself (renderer, keys and
// pointer, movement, camera, the office's WebSocket) is in public/deck/; this file holds the state the office sends,
// voice (one RTCPeerConnection to Cloudflare's SFU), and the DOM panels: chat, who's online, dress-up, game huts, 钓鱼,
// greetings (打招呼), the 会议室 meeting view (public/meeting.js), and 语音转文字 (public/stt.js: captions, transcript lines
// in the chat log, my words to the agents who hear me). Dress-up is public/dress.js.
import { setFlags } from '/flags.js';
import { BODIES, lookFor, buildSheet, otterArtReady } from '/looks.js';
import { initDress } from '/dress.js';
import { initGames } from '/games.js';
import { initGomoku } from '/gomoku.js';
import { initCards } from '/cards.js';
import { initRecords, recordsMsg, openRecords } from '/records.js';
import { initFishing } from '/fishing.js';
import { initPick } from '/pick.js';
import { initDesks } from '/desks.js';
import { addSettingsSection, setSettingsHeader, refreshSettingsHeader, openSettings } from '/settings.js';
import { micPipeline, rawMic, micConstraints, voiceMode, micDevice, denoiseWanted, agoraDenoiseLevel, keepRunning } from '/denoise.js';
import { mountVoiceSettings } from '/voice-settings.js';
import { createSfuAdapter } from '/rtc/sfu.js';
import { createVoiceProvider } from '/rtc/voice-provider.ts';
import { createAgoraAuthorization } from '/rtc/agora-authorization.ts';
import { createAgoraFloor } from '/rtc/agora-floor.ts';
import { parseMap, keepPlacesLocalized, collider, createMover, createCamera, watchHud, bindInput, createNet, loadScene, loadStick, loadRooms, initMapEditor, refreshMap, inEdge, portalTo } from '/deck/index.js';
import { ICONS as EMOTE_ICONS, setEmote, emoteMenu } from '/emotes.js';
import { initGreet } from '/greet.js';
import { initDuo } from '/duo.js';
import { initInvite } from '/invite.js';
import { initPwa } from '/pwa.js';
import { initFullscreen } from '/fullscreen.js';
import { label, put, icon, hydrate } from '/icons.js';
import { initMeeting } from '/meeting.js';
import { initMeetHost } from '/meethost.js';
import { initNotes } from '/notes.js';
import { initMeetInvite } from '/meetinvite.js';
import { initCalendar } from '/calendar.js';
import { initMembers } from '/members.js';
import { initStt, STT_TIP_OTHER } from '/stt.js';
import { initMeetLink } from '/meetlink.js';
import { initSecretary } from '/secretary.js';
import { createLoading } from '/loading.js';
import { initSit } from '/sit.js';
import { DESK_HUSH, circleOf, atDesk } from '/seats.js';
import { initOfficeTour } from '/tours.js';
import { initStatus, statusOf, statusLine, STATES, DND_NEAR } from '/status.js';
import { initGuests } from '/guests.js';
import { initWhiteboard } from '/whiteboard.js';
import { createPrejoin } from '/prejoin.js';
import { initFloors, floorName, refreshFloors, PLAY_FLOOR } from '/floors.js';
import { initBoats } from '/boats.js';
import { initNeighbours } from '/neighbours.js';
import { setSail, takeVoyage, voyageCover, dropReboard } from '/voyage.js';
import { initCall } from '/call.js';
import { initPip } from '/pip.js';
import { initRaftLinks } from '/raft-links.js';
import { initAgentDoor } from '/agent-door.js';
import { initDevBots } from '/devbots.js';
import { initProfile, serverLabel } from '/profile.js';
import { initSounds } from '/sounds.js';
import { VISIT, vq, initVisits, pendingInvite, takeInvite } from '/visits.js';
import { initNotice, takeReboard } from '/notice.js';
import { initRooms } from '/rooms.js';
import { useAccountLocale, t, tr, has, onLang, translateDom, fmtTime } from '/i18n.js';
import { initBar, setBar } from '/toolbar.js';
// Back to the 会议纪要 page that sent me to sign in (public/notes-page.js; only a path of ours, and only within 15
// minutes: an abandoned sign-in doesn't bounce a later visit; review N4)
try {
  const raw = sessionStorage.getItem('notes.back'); sessionStorage.removeItem('notes.back');
  const back = raw ? JSON.parse(raw) : null;
  if (back && typeof back.p === 'string' && Date.now() - Number(back.at) < 15 * 60_000 && /^\/notes\/[A-Za-z0-9_-]{1,40}(\?o=[a-z0-9-]{1,40})?$/.test(back.p)) location.replace(back.p);
} catch {}
// 换船 (public/voyage.js): come from another boat of this office: the sea is already up over the page, and it boards
// by itself (takeReboard below) with the mic as it was; the sea goes down once my otter is on this deck (start), or
// as soon as the page has something else to say (overlay, the sign-in card: it never hides those)
const voyage = takeVoyage(), voyageCoverUp = voyage ? voyageCover(voyage.to, { walk: !!voyage.walk }) : null;
// An OtterBonk room invite (?ob=<room id>, games-otterbonk.js 复制邀请链接): kept for this tab through the sign-in, and
// out of the address bar; the hut module walks me there and into the room once I'm aboard
{
  const u = new URL(location.href), ob = u.searchParams.get('ob');
  if (ob) { if (/^[a-z0-9-]{1,24}$/.test(ob)) try { sessionStorage.setItem('ob.join', ob); } catch {} u.searchParams.delete('ob'); history.replaceState(null, '', u); }
}
const $ = (id) => document.getElementById(id);
// index.html's static text (data-i18n) in the reader's language, then shown (its inline script hid it for a reader
// who isn't on 简体中文, so they never see the Chinese first); later changes redraw it by themselves (i18n.js)
translateDom();
/** el's text from fn(), now and again on every language change (设置 → 语言). Returns el. */
const live = (el, fn) => { const draw = () => { el.textContent = fn(); }; draw(); onLang(draw); return el; };
initPwa({ addSettingsSection }); // the service worker, its update toast, 「安装到桌面」 (public/pwa.js)
initFullscreen({ addSettingsSection }); // a phone: full screen from 上船 on (Android), or how to add Deck to the home screen (iPhone)
// 「经典水獭」 (the hand-drawn 8-direction otters) is gone: a device that had picked it gets the 3D ones (looks.js)
try { localStorage.removeItem('deck.otters'); sessionStorage.removeItem('deck.otters3dFailed'); } catch {}
// A page cached from before a button existed must still work with this script.
for (const [id, before] of [['share', 'gather'], ['dress', 'mic'], ['chat', 'mic'], ['emote', 'mic'], ['arrange', 'share'], ['call', 'cast']]) {
  if (!$(id)) { const b = document.createElement('button'); b.id = id; if (id === 'arrange') b.style.display = 'none'; if (id === 'call') $(before).after(b); else $(before).before(b); }
}
// the bar's labels: a pixel icon and the text, per button and state (public/toolbar.js), in the reader's language
initBar();
// the elements this file draws itself (icons, states): their index.html data-i18n was only for the first paint
for (const el of document.querySelectorAll('.bar [data-i18n], #recenter[data-i18n], #toastGo[data-i18n]')) delete el.dataset.i18n;
document.documentElement.classList.remove('i18n-wait');
$('boat')?.remove(); // boats are automatic now: a page cached from before may still have the old button
if (!$('screens')) { const d = document.createElement('div'); d.id = 'screens'; document.body.appendChild(d); }
// The loading screen (public/loading.js): 上船 shows it (only if loading is still going 250 ms later) until the map has
// drawn its first frames. Its bar is these tasks, weighted roughly by size: what loads from here on (all at once, behind
// the join card), then the map's own loader, its build, and the warm-up frames (in start()).
const loading = createLoading({ title: t('app.loading.title'), waitForLook: true });
let loadingHasWelcome = false; // a late pre-join preview cannot replace the office's actual look
onLang(() => loading.setTitle(t('app.loading.title')));
// --barh: the bar's top edge, measured from the bottom of the screen (safe area included), for everything that sits
// just above it (index.html, the chat here, emotes.js, status.js), so no fixed offset fits.
// On a phone the bar is one row that scrolls sideways (index.html): .more puts the fade on its right edge while buttons
// are out of sight there (a label changing length, a button shown or hidden, the phone turned all change that), and a
// mouse wheel over it scrolls it sideways (a narrow desktop window).
{
  const bar = document.querySelector('.bar');
  const more = () => bar.classList.toggle('more', bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2);
  const put = () => {
    if (!bar) return;
    const r = bar.getBoundingClientRect(); if (r.height) document.documentElement.style.setProperty('--barh', Math.round(innerHeight - r.top) + 'px');
    const mic = document.getElementById('mic'); if (mic) bar.style.setProperty('--micw', mic.offsetWidth + 'px'); // (index.html: 按住说话 pins next to it)
    more();
  };
  if (bar && window.ResizeObserver) {
    const ro = new ResizeObserver(put); ro.observe(bar);
    for (const b of bar.children) ro.observe(b);
    new MutationObserver((ms) => { for (const m of ms) for (const b of m.addedNodes) if (b.nodeType === 1) ro.observe(b); put(); }).observe(bar, { childList: true });
  }
  bar?.addEventListener('scroll', more, { passive: true });
  bar?.addEventListener('wheel', (e) => {
    if (e.ctrlKey || bar.scrollWidth <= bar.clientWidth || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    bar.scrollLeft += e.deltaY; e.preventDefault();
  }, { passive: false });
  addEventListener('resize', put); put();
}
const ld = { preload: loading.task(3), build: loading.task(1), warm: loading.task(1) };
// The map's renderer (Phaser) downloads while the join card is up. If it can't be downloaded, 进入 says so (mapFailed).
const sceneModule = loading.track(loadScene().catch((e) => { console.error('the map failed to load', e); return null; }), 6);
try { localStorage.removeItem('deck.engine'); } catch {} // an old build let a device pick another renderer
// The map (public/static/map.tmj, Tiled): the same file and reader the office uses (deck/map.js); Phaser loads it as a tilemap.
// It, the otter sheets, their accessories and the emotes load side by side, and the join card doesn't wait for them:
// 上船 pressed early just shows the loading screen until they're in (wantGo, at the end of this file).
// The 活动舱 (?floor=PLAY_FLOOR, public/floors.js) stands on its own map, public/static/play.tmj; checked against the floor list
// below, before the map is read.
const wantPlay = new URL(location.href).searchParams.get('floor') === String(PLAY_FLOOR);
const mapOf = (play) => fetch(play ? '/play.tmj' : '/map.tmj').then((r) => r.json()).catch(() => null);
const mapP = loading.track(mapOf(wantPlay), 1);
const otterP = Promise.all(BODIES.map((b) => loading.track(load(b.sheet), 0.5))); // one sheet per body (looks.js BODIES)
loading.track(otterArtReady, 1);
const emoteP = loading.track(load(EMOTE_ICONS), 0.2);
const whoP = fetch(vq('/me')).catch(() => null); // visiting another server's office (串门, ?visit=): who I am there
let wantGo = false;

// ---- join screen
// Offline (an installed Deck opened without network: the service worker gave us the cached page), say so and come
// back by itself when the network does. /me never comes from a cache, so a failed fetch means no network.
const who = await whoP;
if (!who) {
  overlay('offline', 'app.offline.title', 'app.offline.text', 'app.action.tryAgain');
  addEventListener('online', () => location.reload());
  await new Promise(() => {}); // stop here; the reload starts over
}
// The floor (public/floors.js, ?floor=N): its own Office, so the pre-join page asks that one too. Its picker is up
// before this module has finished: a switch then needs nothing stopped (booted, set at the end of this file).
let booted = false;
let voiceFloor = null;
let sailingTo = null, sailingWalk = false; // the boat this page is leaving for (换船), while the sea rises; walking across (the 码头 / 跳板)
const floorP = initFloors({addSettingsSection,toast,canLeave:()=>!booted||!mapEditor.active()||confirm(t('app.map.leaveBoatDraft')),beforeSwitch:(id)=>booted?voiceFloor?.prepare(id):undefined,
  // on board: 换船 without the 上船 card (public/voyage.js): the sea covers the page, the next one boards by itself
  // (what the next page needs is kept as the page leaves, in leave(): a mute pressed under the rising sea counts)
  sail:(name,walk=false)=>{if(!booted||!me)return 0;sailingTo=name;sailingWalk=walk;voyageCover(name,{rise:true,walk});return walk?260:480;},leave:()=>{
  if(!booted)return;
  if(sailingTo)setSail({to:sailingTo,muted,walk:sailingWalk});
  callUi?.moving(); // in a call: kept longer while the next boat loads (office/call.ts MOVE_MS)
  net.close();voiceStopped=true;meeting.stop();stt.stop();stopVoice();
  share.stream?.getTracks().forEach(t=>t.stop());
}});
// what the pre-join page shows (public/prejoin.js): my saved look and status on this floor (src/routes/prejoin.ts).
// 1F is always the office "main" (office/floors.ts), so it doesn't wait for the floor list.
const onFirst = (new URL(location.href).searchParams.get('floor') ?? '1') === '1';
const preP = (onFirst ? Promise.resolve({ room: 'main' }) : floorP)
  .then((f) => (f.room ? fetch(vq('/prejoin/' + encodeURIComponent(f.room))) : null))
  .then((r) => (r?.ok ? r.json() : null)).then((r) => { setFlags(r?.flags); return r; }).catch(() => null); // flags: what's on in this office (flags.js)
// Signed in: the pre-join page (public/prejoin.js): my otter and 装扮, my status, the mic check, then 上船 (or Enter).
// The dress-up panel comes up once it's built (dressReady, at the end of this file); until then 装扮 waits for it.
let prejoin = null, dressReady = null, dressOpen = () => {};
const dressWhen = new Promise((r) => { dressReady = r; });
let myServer = null; // the Raft server I signed in with (routes/auth.ts /me), for public/status.js
let myServerInfo = null; // and its {id, slug, name}, for links into Raft (public/raft-links.js)
// a guest (访客, let in by a member's invite link: office/guests.ts) gets {guest: {by, exp}}: no inviting, broadcasting
// or 集合, and a note on the pre-join page of who invited them
let guestMe = null;
// visiting another server's office (串门, public/visits.js): /me?visit= gave {visit: {server, server_name, from, exp}}
// and home (my own server): as a guest, no inviting, broadcasting or 集合 there, and no links into Raft
let visitMe = null, myHome = null;
let myName = '', myPicture = null, myOthers = []; // for the header of 设置 (public/profile.js); myOthers: my other servers in this browser
if (who.ok) {
  const w = await who.json();
  useAccountLocale(w.locale); // the Raft account's language, if Raft said one (public/i18n.js; 设置 → 语言 wins over it)
  // 串门 that ended while the page was away (or an old ?visit= link): nothing to board here; the button goes home
  if (VISIT && w.visit_ended) {
    overlay('visitEnded', 'app.visit.ended.title', 'app.visit.ended.text', 'app.action.goHome', () => location.replace('/'));
    await new Promise(() => {});
  }
  myServer = w.server?.id ?? null; myServerInfo = w.server ?? null; myName = w.name ?? ''; myPicture = w.picture ?? null; myOthers = Array.isArray(w.others) ? w.others : [];
  prejoin = createPrejoin({ root: $('signedIn'), name: w.name, info: preP, otterImgs: otterP,
    onLook: (look, images) => { if (!loadingHasWelcome) loading.setLook(look, images); },
    openDress: () => dressWhen.then((d) => d.open()), dressOpen: () => dressOpen() });
  $('signedIn').style.display = '';
  if (w.visit) {
    visitMe = w.visit; myHome = w.home ?? null; myServerInfo = null;
    const p = document.createElement('p'); p.style.cssText = 'font-size:12px;color:#444;margin:6px 0 0;text-align:center';
    live(p, () => t('app.visit.note', { server: w.visit.server_name || t('app.visit.otherServer'), from: w.visit.from?.name || t('app.visit.yourServer') }));
    $('signedIn').append(p);
    for (const id of ['gather', 'cast']) if ($(id)) $(id).style.display = 'none'; // the office refuses them (GUEST_DENIED)
  }
  if (w.guest) {
    guestMe = w.guest;
    const p = document.createElement('p'); p.style.cssText = 'font-size:12px;color:#444;margin:6px 0 0;text-align:center';
    live(p, () => t('app.guest.note', { by: w.guest.by || t('app.guest.aMember') }));
    $('signedIn').append(p);
    for (const id of ['gather', 'cast']) if ($(id)) $(id).style.display = 'none'; // the office refuses them anyway (GUEST_DENIED)
  }
}
else {
  $('signedOut').style.display = ''; voyageCoverUp?.done(); dropReboard();
  const err = new URLSearchParams(location.search).get('login_error');
  // a guest whose visit ended (office/guests.ts): /me said so, and dropped the guest cookie
  if ((await who.json().catch(() => null))?.error === 'guest_ended') tr($('loginError'), 'app.login.guestEnded');
  if (err) tr($('loginError'), err === 'outsider' ? 'app.login.err.outsider' : err === 'cancelled' ? 'app.login.err.cancelled' : 'app.login.err.other');
}
$('go').onclick = () => { wantGo = true; $('join').style.display = 'none'; loading.show(); };
// back from 发版预告's soft reload (public/notice.js): straight on board again, as before the update
if (who.ok && takeReboard()) { prejoin?.stop(); $('go').onclick(); }
// 邀请串门 (public/visits.js): a link opened here. Signed in with Raft: go there now (or say why not, on the join
// card); signed out: it waits for the sign-in (Raft brings me back to "/" in this tab).
{
  const inv = guestMe ? null : pendingInvite();
  const note = (root, text) => { const p = document.createElement('p'); p.id = 'visitInviteNote'; p.style.cssText = 'font-size:12px;color:#14532d;background:#dcfce7;border:2px solid #1b1410;border-radius:8px;padding:4px 8px;margin:8px 0 0;text-align:center'; p.textContent = text; root?.append(p); return p; };
  if (inv && who.ok) { const why = await takeInvite(inv); if (why) note($('signedIn'), why); else await new Promise(() => {}); }
  else if (inv) tr(note($('signedOut'), ''), 'app.visit.inviteSignIn');
}

// ---- 退出登录 (artin 10-31): the header of 设置 (public/profile.js), and the bottom of my status menu (the chip in the bar, public/status.js). A
// member signs out on this device; a guest's 「离开 Deck」 (not just 离开: that's a status in the same menu) ends the visit (the guest cookie goes, so the link has to be
// opened again). POST /auth/logout (src/auth.ts logout: same-origin only) drops the cookies; then the socket, voice,
// mic, screen share and captions stop here, then the page goes back to the sign-in card.
// (label: a getter, so 设置's header and the status menu read it in the language of the moment)
const SIGN_OUT = guestMe
  ? { get label() { return t('app.signOut.guest.label'); }, get ask() { return t('app.signOut.guest.ask'); } }
  : { get label() { return t('app.signOut.label'); }, get ask() { return t('app.signOut.ask'); } };
/** Stop everything live here (socket, voice, mic, screen share, captions) before the page goes elsewhere. */
function leaveHere() {
  prejoin?.stop();
  if (booted) try {
    net.close(); me = null; hearing.clear(); voiceStopped = true;
    meeting.stop(); stt.stop(); stt.forget(); whiteboard.stop(); stopVoice(); latency.stop();
    share.stream?.getTracks().forEach((t) => t.stop()); share.mid = null;
  } catch (e) { console.error(e); }
}
// 退出登录 ends every session this browser holds (src/auth.ts logout: the safe one on a shared device). When it's signed
// in to other servers too (myOthers), 设置 also offers 退出这个 Server: this one only, then the most recently used
// other one takes over and the page reloads there.
// The server ends the session too (src/revoke.ts: a copy of the cookie stops working, my sockets close), and the other
// tabs of this browser hear it (deck-auth below) and show 已退出登录. 所有设备都退出 (everywhere): every session of mine
// on this server, on every device.
async function signOut(onlyThis = false, everywhere = false) {
  const more = !guestMe && myOthers.length > 0;
  const ask = everywhere ? t('app.signOut.everywhereAsk')
    : onlyThis ? t('app.signOut.thisAsk', { server: serverLabel(myServerInfo) || t('app.signOut.thisServer'), next: serverLabel(myOthers[0]) || t('app.signOut.otherServer') })
    : more ? t('app.signOut.allAsk', { n: myOthers.length + 1 }) : SIGN_OUT.ask;
  if (!who.ok || !confirm(ask)) return;
  const q = new URLSearchParams();
  if (everywhere) q.set('scope', 'everywhere'); else if (onlyThis && more) q.set('scope', 'current');
  try { if (floor?.room) q.set('room', floor.room); } catch {}
  const qs = String(q);
  const r = await fetch('/auth/logout' + (qs ? '?' + qs : ''), { method: 'POST', cache: 'no-store' }).catch(() => null);
  if (!r?.ok) { toast(t('app.signOut.failed')); return; }
  const res = await r.json().catch(() => null), switched = res?.switched;
  // server: false = signed out on this device, but the server couldn't record it (an office moving): say so
  if (res?.server === false) { toast(t('app.signOut.partial')); await new Promise((ok) => setTimeout(ok, 2500)); }
  signingOut = true;
  if (!switched) try { new BroadcastChannel('deck-auth').postMessage({ t: 'signed_out' }); } catch {}
  leaveHere();
  location.replace('/');
}
// Another tab of this browser signed out (or left, as a guest): this one stops and says so, rather than finding out at
// its next request.
let signingOut = false; // (this tab did: it goes back to the start page itself)
if (who.ok) try {
  new BroadcastChannel('deck-auth').onmessage = (e) => {
    if (e.data?.t !== 'signed_out' || signingOut) return;
    wantGo = false; leaveHere(); try { setNet('off', guestMe ? 'left' : 'signedOut'); } catch {}
    if (guestMe) overlay('signedout', 'app.signedOut.guest.title', 'app.signedOut.guest.text', 'app.action.ok');
    else overlay('signedout', 'app.status.signedOut', 'app.signedOut.otherTab', 'app.action.signInAgain', () => { location.href = '/auth/login'; });
  };
} catch {}
/** This browser's active server isn't this page's any more (切换 Server or a sign-in in another tab): this tab leaves
 *  and says so; the button reloads onto the active one. */
function serverMoved() {
  if ($('serverMoved')) return;
  wantGo = false; leaveHere(); setNet('off', 'serverMoved');
  overlay('serverMoved', 'app.serverMoved.title', 'app.serverMoved.text', 'app.action.reload'); $('serverMoved')?.style.setProperty('z-index', '88');
}
// ---- 切换 Server (artin 11-02; public/profile.js). Raft signs in to one server per login and doesn't list my servers,
// so the list is the servers this browser has signed in to (GET /me others). Switching to one of them: POST
// /auth/switch, no new sign-in; then the page reloads on that server's raft (its own offices, socket and voice).
async function switchTo(key) {
  const r = await fetch('/auth/switch', { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key }) }).catch(() => null);
  if (r?.status === 404) { myOthers = myOthers.filter((o) => o.key !== key); profile?.refresh(); toast(t('app.switch.expired')); return; }
  if (!r?.ok) { toast(t('app.switch.failed')); return; }
  leaveHere();
  location.replace('/');
}
/** 设置, scrolled to 串门 (public/visits.js). */
function openVisits(keyboard) {
  openSettings(keyboard, 'visits');
}
// 添加 Server: optionally name the target for Raft; without a hint Raft asks which server. This session stays active
// until the new one comes back, then joins the list (src/auth.ts callback); turned back at Raft, I'm still here.
function addServer(hint) {
  const q = hint ? '?' + new URLSearchParams({ server: hint }) : '';
  leaveHere();
  location.assign('/auth/login' + q);
}
// ---- who I am, my Raft server, 切换 Server and 退出登录: the header of 设置 and the server's name in the top bar
// (public/profile.js). 退出登录 is also at the bottom of my status menu (public/status.js).
let profile = null;
if (who.ok) {
  profile = initProfile({ name: myName, picture: myPicture, server: guestMe ? null : visitMe ? myHome : myServerInfo, guest: guestMe,
    visit: visitMe, goHome: () => visits.goHome(), signOut: { get label() { return SIGN_OUT.label; }, run: () => signOut() },
    others: () => myOthers, switchTo, addServer: guestMe ? null : addServer, signOutThis: () => signOut(true),
    signOutAll: guestMe ? null : () => signOut(false, true),
    settings: { setHeader: setSettingsHeader, refreshHeader: refreshSettingsHeader, open: openSettings },
    // (me and otterImgs are declared further down: until then, 在线 and no otter)
    status: () => { let s = null; try { s = me ? statusOf(me) : null; } catch {} return { icon: STATES[s?.s ?? 'available'].icon, text: statusLine(s) }; },
    avatar: () => {
      try { if (!me || !otterImgs) return null; } catch { return null; }
      const c = document.createElement('canvas'); c.width = c.height = 48;
      const g = c.getContext('2d'); g.imageSmoothingEnabled = false;
      g.drawImage(buildSheet(otterImgs, lookOf(me)), 0, 0, 32, 32, 0, 0, 48, 48);
      return c;
    } });
  // Other tabs: the cookie holds one active server for the whole browser, so a tab that switched (or signed in to
  // another server, or out of this one) says which server is active now, and a tab still showing another one reloads
  // (its socket would go to the new server's office on its next reconnect, under this page's old names).
  // (not while visiting: the visit is this tab's own, chosen by its address)
  if (!guestMe && !visitMe && myServer) try {
    const bc = new BroadcastChannel('deck-server');
    // not a reload by itself (a map draft, a meeting): stop here and say so; the button reloads
    // A message can be stale (a tab that read /me just before the switch): ask /me which server is active now; if it's
    // still mine, say so again (the stale tab then stops instead).
    bc.onmessage = async (e) => {
      if (e.data?.server === myServer) return;
      const now = await fetch('/me', { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => undefined);
      if (now === undefined) return; // no network: the next message or reconnect decides
      if (now?.server?.id === myServer) { bc.postMessage({ server: myServer }); return; }
      serverMoved();
    };
    bc.postMessage({ server: myServer });
  } catch {}
  // visiting (串门): another tab switching server or signing out ends this tab's visit (src/routes/auth.ts); listen
  // only (this tab's server is the one it visits, not the browser's), and ask /me?visit= whether the visit still holds
  if (visitMe) try {
    new BroadcastChannel('deck-server').onmessage = async () => {
      const now = await fetch(vq('/me'), { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => undefined);
      if (now === undefined || now?.visit) return;
      if (booted) visitOff(4009); else { wantGo = false; overlay('visitEnded', 'app.visit.ended.title', 'app.visit.ended.otherTab', 'app.action.goHome', () => location.replace('/')); }
    };
  } catch {}
  // the pre-join card: which server's raft this is, and the way to another (opens 设置 and its header)
  if (!guestMe && !visitMe && myServerInfo) {
    const name = serverLabel(myServerInfo) || 'Raft server';
    const p = document.createElement('p'); p.id = 'joinServer'; p.style.cssText = 'font-size:12px;color:#444;margin:8px 0 0;text-align:center;display:flex;gap:6px;justify-content:center;align-items:center;flex-wrap:wrap';
    const b = document.createElement('b'); b.textContent = name; b.style.cssText = 'max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
    const a = document.createElement('button'); a.type = 'button'; tr(a, 'app.join.switchServer');
    a.onclick = (e) => { e.stopPropagation(); openSettings(e.detail === 0, 'account'); };
    a.style.cssText = 'width:auto;font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:1px 10px;background:#fff;cursor:pointer';
    // 去串门 (public/visits.js): the offices of other servers open to mine, in 设置 → 串门
    const v = a.cloneNode(); v.id = 'joinVisit'; tr(v, 'app.visit.go', undefined, { title: 'app.visit.goTip' });
    v.onclick = (e) => { e.stopPropagation(); openVisits(e.detail === 0); };
    p.append('Server', b, a, v);
    $('signedIn').append(p);
  }
}

let mapJson = await mapP;
{ const f = await floorP; if (mapJson && f.room && (f.kind === 'play') !== wantPlay) mapJson = await mapOf(f.kind === 'play'); }
// The map didn't come (offline, a 5xx, not JSON): say so instead of a loading screen that never ends, and stop here.
if (!mapJson) { overlay('mapfail', 'app.mapFail.title', 'app.loadFail.text', 'app.action.retry'); await new Promise(() => {}); }
const MAP = parseMap(mapJson);
const BASE_MAP = structuredClone(MAP);
keepPlacesLocalized(MAP, BASE_MAP); // place names in the reader's language, now and on every change (deck/places.js)
let scene = null;
let pointer = null; // the map's pointer helpers (deck/input.js): the hover outline and a click's ripple, for the scene
const otterImgs = await otterP;
// A body sheet that didn't load (a flaky network): ask once more, cache-busted. Still missing: say so with a retry
// (as for the map) rather than start with invisible otters. There is no other art to fall back on since 「经典水獭」 went.
for (const [i, img] of otterImgs.entries()) if (!img) otterImgs[i] = await load(BODIES[i].sheet + (BODIES[i].sheet.includes('?') ? '&' : '?') + 'retry=' + Date.now());
if (otterImgs.some((i) => !i)) { overlay('otterfail', 'app.otterFail.title', 'app.loadFail.text', 'app.action.retry'); await new Promise(() => {}); }
await otterArtReady; // the 3D otters' accessory overlays (looks.js), so every sheet built from here on has them
const emoteImg = await emoteP; // the emotes' icons (public/emotes.js)
function load(src) { return new Promise((r) => { const i = new Image(); i.onload = () => r(i); i.onerror = () => r(null); i.src = src; }); } // null: it didn't load
const { zoneAt } = collider(MAP); // the room an otter is in, by its feet (the same rule as the office)


// ---- state
const peers = new Map(); // id -> {name, variant, x,y,dir,moving, drawX, drawY, sessionId, trackName, hear, gain}
let rally = null;
let me = null, hearing = new Set(), caster = null;
// (my look and status picked on the pre-join page are sent on the welcome: prejoin.flush)
/** The gather flag, while it stands. */
const rallyNow = () => (rally && Date.now() < rally.until ? rally : (rally = null));
const floor = await floorP;
// come across from the other boat (public/boats.js): land by its 码头 / 跳板 (the office reads ?arrive=); a reload doesn't
const arrive = new URL(location.href).searchParams.get('arrive');
if (arrive) { const u = new URL(location.href); u.searchParams.delete('arrive'); history.replaceState(history.state, '', u); }
let landed = false;
const net = createNet({ path:()=>vq('/ws/'+encodeURIComponent(floor.room??'main')+(!landed&&arrive&&/^[a-z0-9-]{1,40}$/.test(arrive)?'?arrive='+arrive+'&shore=1':'')),
  onOpen: () => { loading.note(''); send({ t: 'hello', name: '' }); latency.start(); },
  onMessage: (m) => { if (m.t === 'welcome') landed = true; onMessage(m); }, onClose, onPong: () => latency.pong() });
const send = net.send;
initRecords({ send, off: !!guestMe || !!visitMe }); // 战绩 / 排行榜 (public/records.js, office/records.ts)
$('go').onclick = start; // 上船 pressed before this module finished is handled at the end.
// Back from the background (a phone's app switcher, a laptop waking up, an installed Deck brought to front): the socket
// may have died without telling us; check it now rather than wait for the 3 s retry, or for a dead one to time out.
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') net.resume(); });
addEventListener('pageshow', (e) => { if (e.persisted) net.resume(); });
addEventListener('online', () => net.resume());
let meetHost = null; // 主持人 (public/meethost.js, made with the meeting view further down)
// the gate: a 会议室 its host locked first (public/meethost.js: not through that door, so a 通话 isn't ended for it), then
// no walking into a 会议室 in a 通话 without asking (public/call.js)
const mover = createMover({ MAP, me: () => me, send, toast, t, mayEnter: (x0, y0, x, y) => (meetHost?.mayEnter(x0, y0, x, y) ?? true) && (callUi?.mayEnter(x0, y0, x, y) ?? true) });
initBoats({ MAP, me: () => me, floor }); // the 码头 / 跳板 to the other boat
const nb = initNeighbours({ MAP, me: () => me, peers: () => peers, floor, setEmote, inEdge, portalTo, drop: (q) => dropMedia(q) }); // 邻船: the people at the other boat's edge (office/edge.ts)
// chairs, benches, desks, meeting tables (public/sit.js): E or a tap to sit, walking off to stand
const mapEditor = initMapEditor({ base: BASE_MAP, send, toast, addSettingsSection,
  stop: () => { mover.keys.clear(); mover.unpush(); mover.clear(); },
  // the collider first (Phaser's tiles), then a trip under way finds its way again on the new map
  apply: (map) => { Object.assign(MAP, map); refreshMap(scene, MAP); mover.replan(); pick.mapChanged(); }, // a selected seat or room may have moved
});
const sit = initSit({ MAP, me: () => me, peers: () => peers, send, mover, toast, gameTables: () => gomoku.tables() });
// 我的工位 (public/desks.js): who owns which desk, claiming / releasing / decorating one, 回到我的工位 on the way in
const desks = initDesks({ MAP, me: () => me, send, sit, toast, mover, ready: () => !!me && !!scene?.otters?.has(me) });
const camera = createCamera({ tile: MAP.tile, map: MAP, // (the live map: an edit may grow it, map-overlay.js applyOverlay)
  onFree: (free) => { $('recenter').style.display = free ? '' : 'none'; } });
watchHud(camera); // the view keeps clear of the bars (deck/hud.js)
// A button clicked with the mouse shouldn't keep focus: the next Enter (or WASD, which Chrome then treats as keyboard
// use of that button) belongs to the map. Buttons reached with Tab keep it.
addEventListener('click', (e) => { const b = e.target.closest?.('button'); if (b && e.detail > 0) b.blur(); }, true);

async function start() {
  if(!floor.room){loading.close();voyageCoverUp?.done();dropReboard();$('join').style.display='';return toast(floor.error);}
  prejoin?.stop();
  $('join').style.display = 'none';
  loading.show();
  const phaser = await sceneModule; // already here unless the download is slow
  if (!phaser) return mapFailed();
  net.connect();
  // Voice must never keep anyone off the deck. The Office's welcome chooses the provider for the whole server.
  try { ({ scene } = await phaser.startScene({ canvas: $('c'), MAP, mapJson, otterImgs, emoteImg, camera, view, onPreload: (load) => loading.phaser(load, ld.preload) })); }
  catch (e) { console.error('the map failed to start', e); return mapFailed(); }
  ld.preload.done(); ld.build.done();
  // the loading screen goes once my otter is drawn (its sheet built: that's what the office's welcome brings; at most
  // 2.5 s, so a slow or moving office never keeps anyone under it: 服务器搬家中 shows on it and on the status) and a
  // few frames have gone by, so textures are on the GPU and shaders compiled before the first frame anyone sees
  loading.ready(() => !!me && scene.otters?.has(me), { task: ld.warm }).then(() => voyageCoverUp?.done());
  (window.__deck ??= {}).camera = camera; // for debugging from the console
  mover.setCollider(scene.collide); // collisions from Phaser's tilemap (same map, same answers as the grid and the office)
  // the phone's stick (deck/stick.js): hidden while walking is off anyway (and under any panel or game, and while typing)
  const stick = await loadStick().then(({ createStick }) => createStick({ scene, canvas: $('c'), camera, mover, me: () => me, addSettingsSection,
    blocked: () => meeting.full() || mapEditor.active() || fishing.active() || rooms.on() || !!(me && sit.seatOf(me)) })).catch((e) => { console.error('no stick', e); return null; });
  pointer = bindInput({ scene, canvas: $('c'), camera, mover, me: () => me, ui: {
    chatOpen, chatClose, chatShown: () => $('chatbox')?.style.display !== 'none', toggleMute: () => setMute(!muted), rally: rallyNow, goRally,
    blockWalk: () => meeting.full() || mapEditor.active() || rooms.on(), // (房间 covers the map: no walks or picks under it)
    mapEdit: (phase, x, y) => mapEditor.pointer(phase, x, y),
    tableClick: (tx, ty) => gomoku.tableClick(tx, ty) || cards.tableClick(tx, ty), emote: playEmote, emoteMenu: () => emotes.open(),
    // a deck 白板 first (E next to it, a double click on it: public/whiteboard.js), then seats
    seatClick: (wx, wy) => whiteboard.mapClick(wx, wy) || sit.seatClick(wx, wy), interact: () => duo.key() || whiteboard.interact() || sit.interact() || duo.open(), // E: a 双人互动 request or picker on screen first, then 白板 / seats, then the 互动 prompt (public/duo.js)
    pick: (wx, wy) => pick.click(wx, wy), walked: () => pick.walked(), hoverAt: (wx, wy) => pick.hover(wx, wy),
    tipAt: (x, y) => { const h = scene.tagIconAt?.(x, y); return h ? view.tagTip(h.p, h.name) : null; }, stick } });
  rooms.ready(); // 地图 | 房间 on a phone (public/rooms.js)
}
/** No map (it couldn't be downloaded or started): rather than a blank deck nobody can walk on, leave the office
 *  (others would see an otter standing still), stop voice, and offer a reload. */
function mapFailed() {
  me = null; net.close(); voiceStopped = true; stopVoice();
  latency.stop(); setNet('off', 'mapFailed');
  overlay('mapfail', 'app.mapFail.title', 'app.loadFail.text', 'app.action.retry');
}
/** A card over the deck (styled like the join card) with one button: a reload unless go says otherwise. title, text,
 *  button: catalog keys (vars: their placeholders), so the card follows 设置 → 语言. */
function overlay(id, title, text, button, go = () => location.reload(), vars) {
  loading.close(); // an error card is never hidden behind the loading screen
  voyageCoverUp?.done(); dropReboard(); // (nor behind the 换船 sea; and a reload from here shows the 上船 card)
  if ($(id)) return;
  const d = document.createElement('div'); d.id = id; d.className = 'overlay';
  const card = document.createElement('div'); card.className = 'card';
  const h = tr(document.createElement('h3'), title, vars);
  const p = tr(document.createElement('p'), text, vars);
  const b = tr(document.createElement('button'), button, vars); b.onclick = go;
  card.append(h, p, b); d.append(card); document.body.append(d);
}
/** Are p's words being turned into text right now (mine: stt.js; the others': the office's stt_on) */
const transcribed = (p) => !!p && (p === me ? stt.on() : !!p.stt);
/** What the map shows (deck/scene.js reads it every frame). */
const view = {
  mapSelection:()=>mapEditor.selection(),
  /** what a click selected (public/pick.js): a ring at its foot or a box round a room */
  pickMark: () => pick.mark(),
  /** what the mouse is over and a click's ripple (deck/input.js) */
  hover: (now) => pointer?.hover(now) ?? null,
  me: () => me, peers: () => nb.all(), zoneAt, rally: rallyNow, // (with the other boat's people at its edge: public/neighbours.js)
  speaking, lookOf, tables: () => gomoku.tables(), cardTables: () => cards.tables(),
  /** every frame, after the otters: what 双人互动 shows over heads (public/duo.js; deck/duo-view.js draws it) */
  overlay: (cam, now, drawn) => duo.overlay(cam, now, drawn),
  /** the seat an otter sits on (deck/pose.js sits it), and what to hint at near a free one (deck/seats-view.js) */
  seatOf: (p) => sit.seatOf(p), seats: () => sit.hint(),
  /** the deck 白板 I can open from here (its sign lights up: deck/whiteboards-view.js) */
  boardNear: () => whiteboard.near(),
  /** a 会议室's plaque: 「会议中」 / 「15:00 有会」 after its name (日历, public/calendar.js) */
  roomTag: (zid) => [meetHost?.roomTag(zid), calHook.c?.roomTag(zid)].filter(Boolean).join(' · ') || null, // 已锁门 (public/meethost.js)
  /** the owned desks: their name plates and decorations (public/desks.js, deck/seats-view.js) */
  desks: () => desks.drawn(),
  tick: (dt) => { if (me) { mover.step(dt); enforceShareScope(); const f = gomoku.facing() ?? cards.facing(); if (f != null) { me.dir = f; me.dir8 = f * 2; me.dir16 = f * 4; } volumes(); } },
  /** The name tag's text: my push-to-talk state's words (正在说话), then the name, then 「· 工作中」 sitting at a desk.
   *  (Being transcribed is an icon: tagIcons.) */
  label(p) {
    const b = !p.muted && p === me ? voice.badge() : null, s = statusOf(p);
    // a status set by hand (忙碌 / 请勿打扰) wins over 工作中 at a desk
    const sec = secretary.tag(p); // 秘书: 在听 (the agent) / 对话中 (whoever talks to its secretary), public/secretary.js
    return (b?.text ? b.text + ' ' : '') + (sec ? p.name + ' · ' + sec : s ? p.name + ' · ' + STATES[s.s].name : atDesk(MAP, p) ? t('app.tag.atDesk', { name: p.name }) : p.name);
  },
  /** The pixel icons in front of it (public/icons): broadcasting, muted (or my push-to-talk state), someone I hear,
   *  转写中 (their words are being turned into text, 语音转文字: so nobody is transcribed unawares), then the robot
   *  right before an agent's name. */
  tagIcons(p) {
    const heard = p === me || hearing.has(p.id) || p.id === caster, b = p === me ? voice.badge() : null, out = [], s = statusOf(p);
    if (s) out.push(STATES[s.s].icon); // 离开 / 忙碌 / 请勿打扰 (public/status.js), first
    if (p.id === caster) out.push('megaphone');
    if (p.muted) out.push('mic_off'); else if (b) out.push(b.icon); else if (heard && p !== me && p.id !== caster) out.push('speaker');
    if (transcribed(p)) out.push('transcribing');
    if (desks.owns(p)) out.push('desk'); // has a desk here (我的工位, public/desks.js)
    if (p.kind === 'agent') out.push('robot');
    if (meetHost?.isHost(p)) out.push('crown'); // the host of the 会议室 they stand in (public/meethost.js), right before the name
    return out;
  },
  /** A name tag icon's tooltip (hover, or a long press on touch; deck/scene.js), or null for none. */
  tagTip(p, name) {
    if (name === 'transcribing') return p === me ? stt.tip() : STT_TIP_OTHER;
    if (name === 'desk') return p === me ? t('app.tag.myDesk') : t('app.tag.desk', { name: p.name });
    const st = statusOf(p); // their 离开 / 忙碌 / 请勿打扰 icon (public/status.js)
    if (st && STATES[st.s].icon === name) return t('app.tag.status', { name: p === me ? t('app.tag.me') : p.name, status: statusLine(st), note: st.s === 'dnd' ? 'dnd' : st.auto ? 'auto' : 'none' });
    return null;
  },
  badge(p) {
    const heard = p === me || hearing.has(p.id) || p.id === caster;
    // someone on 请勿打扰 isn't heard, but near me their tag stays clear (so the 请勿打扰 on it reads)
    const dndNear = me && statusOf(p)?.s === 'dnd' && !zoneAt(p.x, p.y) && !zoneAt(me.x, me.y) && Math.hypot(p.x - me.x, p.y - me.y) <= DND_NEAR;
    // a guest's tag (访客: its name ends in 「（访客）」) is light blue, so it reads as one at a glance
    // and a visitor's from another server (串门: 「（来自 …）」) light green
    return { fill: p === me ? 0xfdd03b : p.guest ? 0xdbeafe : p.visitor ? 0xdcfce7 : 0xfff8e7, alpha: p === me || heard || dndNear ? 1 : 0.6 };
  },
};

// ---- 发版预告 (public/notice.js): a banner with a countdown before an update, then a soft reload onto the new version
const notice = initNotice({ aboard: () => !!me && !sailingTo, busy: () => !!share.mid }); // (not while leaving for another boat)
let sttWelcome = null, sttNeed = null, sttCaptureWanted = false;
function onClose(e, reconnect) {
  sttWelcome = null; sttNeed = null; sttCaptureWanted = false;
  notice.down(); // 发版预告: a drop past the update's time is its restart (public/notice.js)
  voiceAuthorization.offline(); // Agora authorization belongs to the lost office socket.
  voiceFloor?.offline();
  mapEditor.disconnected();
  stt.down(); // final lines wait for the next welcome (public/stt.js)
  peers.clear(); nb.clear();
  if (e.code === 4000) return replaced();
  if (e.code === 4001) return signedOut(true); // signed out on the server (退出登录 in another tab or device: src/revoke.ts)
  if (e.code === 4005 || e.code === 4007) return guestOff(e.code); // a guest's visit ended, or the office is full of guests
  if (e.code === 4009 || e.code === 4010) return visitOff(e.code); // 串门: the visit ended, or the office is full of visitors
  latency.stop(); setNet('retry', e.code === 4004 ? 'moving' : 'dropped'); loading.note(netWhy());
  // The sign-in ran out (30 days) or was dropped while the page was open: the socket can't say why it was refused, so
  // ask /me. Signed out: stop and say so rather than retry every 3 s forever (each retry another 401). 4004 is a move.
  // A guest whose visit ended gets /me 401 guest_ended: that's guestOff's card, not a sign-in.
  const retry = setTimeout(reconnect, 3000);
  if (e.code !== 4004) fetch(vq('/me'), { cache: 'no-store' }).then(async (r) => {
    // visiting (串门): /me says whether the visit still holds (signed in at home either way)
    if (visitMe && r.ok) { if ((await r.json().catch(() => null))?.visit_ended) { clearTimeout(retry); visitOff(4009); } return; }
    // this browser's active server changed meanwhile (切换 Server): the reconnect would land in that server's office
    // under this page's names, and its voice too; stop and say so instead
    if (r.ok && !guestMe && myServer) { const w = await r.json().catch(() => null); if (w?.server?.id && w.server.id !== myServer) { clearTimeout(retry); serverMoved(); } return; }
    if (r.status !== 401) return;
    clearTimeout(retry);
    const body = await r.json().catch(() => null);
    if (body?.error === 'guest_ended') guestOff(4005); else signedOut();
  }).catch(() => {});
}
/** Signed out while in the office: stop walking, hearing and sending, and offer to sign in again. `out`: by 退出登录
 *  (somewhere else), not by the sign-in running out. */
function signedOut(out = false) {
  net.close(); // no reconnect on its own later (back to the tab, network back): each would be another 401
  me = null; hearing.clear(); voiceStopped = true; callUi?.stop(); meeting.stop(); stt.stop(); stt.forget(); whiteboard.stop(); stopVoice();
  if (share.mid) { share.mid = null; share.stream?.getTracks().forEach((t) => t.stop()); }
  $('share').classList.remove('live'); setBar('share');
  latency.stop(); setNet('off', out ? 'signedOut' : 'expired');
  overlay('signedout', out ? 'app.status.signedOut' : 'app.status.expired', out ? 'app.signedOut.elsewhere' : 'app.signedOut.expired',
    'app.action.signInAgain', () => { location.href = '/auth/login'; });
}
/** A newer tab of mine took over: this one stops walking, hearing and sending (mic, screen) and says so, with a way
 *  back (a reload, which takes over again). Left as it was, it kept a map you could walk on that nobody else saw. */
function replaced() {
  me = null; hearing.clear(); voiceStopped = true; callUi?.stop(); meeting.stop(); stt.stop(); whiteboard.stop(); stopVoice();
  if (share.mid) { share.mid = null; share.stream?.getTracks().forEach((t) => t.stop()); }
  $('share').classList.remove('live'); setBar('share');
  latency.stop(); setNet('off', 'replaced');
  overlay('replaced', 'app.replaced.title', 'app.replaced.text', 'app.action.continueHere');
}
/** A guest's visit ended (the invite was revoked or ran out: 4005), or there's no room for another guest (4007): stop
 *  like replaced(), and say so; the button reloads (the join card then says what's left to do). */
function guestOff(code) {
  net.close(); // as signedOut(): back on the tab, a reconnect would be a 401 and show 登录已过期 to a guest without a Raft account
  me = null; hearing.clear(); voiceStopped = true; callUi?.stop(); meeting.stop(); stt.stop(); whiteboard.stop(); stopVoice();
  if (share.mid) { share.mid = null; share.stream?.getTracks().forEach((t) => t.stop()); }
  $('share').classList.remove('live'); setBar('share'); // as signedOut(), replaced() (Imprint's review of #138)
  latency.stop(); setNet('off', code === 4005 ? 'guestEnded' : 'guestFull');
  if (code === 4005) overlay('guestEnded', 'app.status.guestEnded', 'app.guestEnded.text', 'app.action.ok');
  else overlay('guestFull', 'app.guestFull.title', 'app.guestFull.text', 'app.action.tryAgain');
}
/** 串门 (public/visits.js): the visit ended (that office closed to my server, it ran out, or 回家 in another tab:
 *  4009), or there's no room for another visitor there (4010): stop like guestOff(); the button goes home. */
function visitOff(code) {
  net.close();
  me = null; hearing.clear(); voiceStopped = true; meeting.stop(); stt.stop(); whiteboard.stop(); stopVoice();
  if (share.mid) { share.mid = null; share.stream?.getTracks().forEach((t) => t.stop()); }
  $('share').classList.remove('live'); setBar('share');
  latency.stop(); setNet('off', code === 4009 ? 'visitEnded' : 'visitFull');
  if (code === 4009) {
    if (visitMe?.server_name) overlay('visitEnded', 'app.visit.ended.title', 'app.visit.ended.named', 'app.action.goHome', () => visits.goHome(), { server: visitMe.server_name });
    else overlay('visitEnded', 'app.visit.ended.title', 'app.visit.ended.there', 'app.action.goHome', () => visits.goHome());
  }
  else overlay('visitFull', 'app.visitFull.title', 'app.visitFull.text', 'app.action.goHome', () => visits.goHome());
}
function onMessage(m) {
  officeSounds.msg(m); // the little sounds (public/sounds.js): it compares with the last state, so before the rest
  if (m.t === 'welcome') {
    setFlags(m.flags); // the feature flags on in this office (flags.js; dress-up hides what's off)
    mapEditor.welcome(m.mapOverlay);
    raftLinks.welcome(m);
    notes.welcome(m); // 会议纪要: which rooms are recording (public/notes.js)
    me = { ...m.me, drawX: m.me.x, drawY: m.me.y, emote: null };
    sttWelcome = m; sttNeed = null;
    // Install the Office's storage generation before welcome starts the first Agora authorization request.
    voiceFloor?.welcome(m.flags, m.voiceStateEpoch);
    voiceAuthorization.welcome(m.flags, me.id);
    for (const p of m.peers) addPeer(p);
    duo.clear(); duo.welcome(me, peers); // pairs mid-action when I came in (the office sends my open requests again)
    setCaster(m.broadcast);
    if (local.trackName) send({ t: 'track', sessionId: local.sessionId, trackName: local.trackName });
    if (muted) { me.muted = true; send({ t: 'mute', on: true }); }
    // a reconnect: the office made a new otter that knows nothing of my screen or camera, so say them again
    if (share.mid && share.name) send({ t: 'screen', trackName: share.name });
    meeting.welcome();
    meetHost.welcome(); // 主持人: the office sends the rooms' hosts again (public/meethost.js)
    callUi?.welcome(); // back into my 通话 after a reload or from another floor (public/call.js)
    if (!audioProvider.isAgora()) stt.welcome(m); // Agora resumes after the matching microphone grant is active
    latency.welcome(m.net);
    fishing.reset();
    tours.welcome(); // the first time on board: the office's coach marks (public/tours.js)
    presence.refresh(); // my status came back with me (office/status.ts)
    whiteboard.welcome(); // the new connection has no board open: open mine again (public/whiteboard.js)
    agentDoor.welcome(m); // the office's agent 上限 (public/agent-door.js)
    secretary.welcome(m); // 秘书: on here, and who talks to whose (public/secretary.js)
    try { meetLink.welcome(); } catch {} // 参会链接 that brought me here (public/meetlink.js; before it's set up: at the end below)
    // what I changed on the pre-join page before boarding (public/prejoin.js): my status, my look
    const l = prejoin?.flush(send); if (l) me.look = l;
    loadingHasWelcome = true; loading.setLook(lookOf(me), otterImgs);
    calendar.welcome(); // 日历: my next meeting on its button, and a walk to a room asked for on the floor before (public/calendar.js)
    desks.welcome(m.desks); // 我的工位: the desks, and 回到我的工位 the first time in (public/desks.js)
    notice.welcome(m); // 发版预告: an update coming (or under way), its banner and the soft reload (public/notice.js)
  } else if (m.t.startsWith('agora_')) { voiceFloor?.message(m); voiceAuthorization.message(m); }
  else if (m.t.startsWith('map_')) { mapEditor.message(m); if (m.t === 'map_permission') raftLinks.permission(m); }
  else if (m.t === 'raft_ch' || m.t === 'raft_ch_err') raftLinks.msg(m); // 会议室 ↔ Raft 频道 (office/raftChannels.ts)
  else if (m.t === 'desks' || m.t === 'desk_err' || m.t === 'desk_admin') desks.msg(m);
  else if (m.t === 'join') addPeer(m.peer);
  else if (m.t === 'leave') { nb.gone(peers.get(m.id)); dropMedia(peers.get(m.id)); peers.delete(m.id); }
  else if (m.t === 'nb' || m.t === 'nb_to') nb.msg(m); // 邻船 (public/neighbours.js, office/edge.ts)
  else if (m.t === 'pos') Object.assign(peers.get(m.id) || {}, { x: m.x, y: m.y, dir: m.dir, dir8: m.dir8, dir16: m.dir16, moving: m.moving, boat: m.boat });
  else if (m.t === 'track') {
    const p = peers.get(m.id);
    if (p) {
      // a new track (an agent's 秘书 speaks with a new one each conversation; null when it ends): let go of the old pull
      const changed = p.mid && (p.sessionId !== m.sessionId || p.trackName !== m.trackName);
      p.sessionId = m.sessionId; p.trackName = m.trackName;
      if (changed && !p.closing) { p.closing = true; serial(() => unpull(p)); }
      syncAudio();
    }
  }
  else if (m.t === 'hear') { hearing = new Set(m.ids); syncAudio(); }
  else if (m.t === 'sfu_reset' && m.sessionId === local.sessionId && !audioProvider.isAgora()) voiceLost();
  else if (m.t === 'screen') { const p = peers.get(m.id); if (p) { p.screenTrack = m.trackName; if (m.sessionId) p.sessionId = m.sessionId; syncAudio(); } }
  else if (m.t === 'error') officeError(m);
  else if (m.t === 'cam') { // a camera in a 会议室 (public/meeting.js); null for me: the office took mine down
    if (m.id === me?.id) { if (!m.trackName) meeting.camDropped(); }
    else { const p = peers.get(m.id); if (p) { p.camTrack = m.trackName; if (m.sessionId) p.sessionId = m.sessionId; syncAudio(); meeting.refresh(); } }
  }
  else if (m.t === 'mute') { const p = peers.get(m.id); if (p) p.muted = m.muted; }
  else if (m.t === 'look') { const p = m.id === me?.id ? me : peers.get(m.id); if (p) p.look = m.look; }
  else if (m.t === 'look_err') toast(t('dress.saveFailed'));
  else if (m.t === 'emote') { const p = m.id === me?.id ? me : peers.get(m.id); if (p) setEmote(p, m.emote, m.ms); }
  else if (m.t === 'emote_err') toast(t('app.emote.tooFast'));
  else if (m.t === 'seat' || m.t === 'seat_err') sit.msg(m); // someone sat down / stood up (public/sit.js)
  else if (m.t === 'greet' || m.t === 'greet_ok' || m.t === 'greet_err') greet.msg(m);
  else if (m.t.startsWith('duo_')) duo.msg(m); // 双人互动: requests, answers, pairs playing (public/duo.js, office/duo.ts)
  else if (m.t === 'invite' || m.t.startsWith('invite_')) invite.msg(m); // a game invite to me, or how mine went
  else if (m.t === 'call' || m.t.startsWith('call_')) callUi?.msg(m); // 通话 (public/call.js, office/call.ts)
  else if (m.t.startsWith('meetinv')) meetinv.msg(m); // inviting people into a 会议室 (public/meetinvite.js)
  else if (m.t.startsWith('g_') || m.t.startsWith('s_') || m.t.startsWith('w_') || m.t.startsWith('m_') || m.t.startsWith('ob_')) games.msg(m); // m_: 心灵捕手
  else if (m.t === 'gm_face') { if (me) { me.dir = m.dir; me.dir8 = m.dir8 ?? m.dir * 2; me.dir16 = m.dir16 ?? me.dir8 * 2; } } // seated at a table: the office turns me to the board
  else if (m.t.startsWith('gm_')) gomoku.msg(m);
  else if (m.t.startsWith('ct_')) cards.msg(m); // 牌桌: 德州扑克 … (public/cards.js, office/tables/cards.ts)
  else if (m.t.startsWith('f_')) fishing.msg(m);
  else if (m.t === 'rec') recordsMsg(m); // 战绩 / 排行榜: an answer to my rec_get (public/records.js)
  else if (m.t === 'cal') calendar.msg(m); // 日历: an invite, a change, a reminder (public/calendar.js, office/calendar.ts)
  else if (m.t.startsWith('notes_')) notes.msg(m); // 会议纪要 (public/notes.js, office/notes.ts)
  else if (m.t.startsWith('wb_')) whiteboard.msg(m); // 白板 (public/whiteboard.js, office/whiteboard.ts)
  else if (m.t === 'gather') {
    rally = { x: m.x, y: m.y, by: m.by, until: m.until };
    if (m.id !== me.id) toast(t('app.gather.by', { name: m.by }), () => goRally());
    else toast(t('app.gather.mine'));
  }
  else if (m.t === 'you') { Object.assign(me, { x: m.x, y: m.y, boat: m.boat, hop: null }); mover.clear(); enforceShareScope(); } // the office put me back
  else if (m.t === 'chat') { const p = m.id === me?.id ? me : peers.get(m.id); if (p) p._awakeAt = performance.now(); chatGot(m); } // talking wakes a sleeping otter
  else if (m.t === 'chat_log') meeting.chatLog(m); // a 会议室's chat so far, as I walk in (office/chat.ts)
  else if (m.t === 'voice') playClip(m);
  // 语音转文字 (public/stt.js, office/stt.ts)
  else if (m.t === 'stt_on') { const p = m.id === me?.id ? me : peers.get(m.id); if (p) p.stt = m.on; meeting.refresh(); if (onlineLast) showOnline(onlineLast); }
  else if (m.t === 'stt_need') { sttNeed = m; if (!audioProvider.isAgora() || audioProvider.sttAvailable()) stt.need(m); }
  else if (m.t === 'caption') { if (!audioProvider.isAgora() || audioProvider.sttAvailable()) captionGot(m); }
  else if (m.t === 'stt_ack' || m.t === 'stt_err') { if (!audioProvider.isAgora() || audioProvider.sttAvailable()) stt.msg(m); }
  else if (m.t.startsWith('meet_host')) meetHost.msg(m); // 主持人, 锁门, 认领主持人 (public/meethost.js, office/meethost.ts)
  else if (m.t === 'meet_links' || m.t === 'meet_ok' || m.t === 'meet_err') meetLink.msg(m); // 参会链接 (public/meetlink.js)
  else if (m.t === 'sec' || m.t === 'sec_on') secretary.msg(m); // 秘书: 对话中 / 在听, an agent's secretary on or off
  else if (m.t === 'online') showOnline(m);
  else if (m.t === 'agent_limit' || m.t === 'agent_kicked') agentDoor.msg(m); // agent 上限, 请出去 (public/agent-door.js)
  else if (m.t === 'status') presence.msg(m); // someone's 在线 / 离开 / 忙碌 / 请勿打扰 (public/status.js)
  else if (m.t === 'status_at') presence.at(m); // when I last chose my status here (floors carry it, public/status.js)
  else if (m.t === 'status_err') presence.err(m); // my change was refused (too fast, a time already past)
  else if (m.t === 'tours') tours.sync(m); // the tours I've seen, kept on my account (office/tours.ts)
  else if (m.t === 'chat_err') toast(m.reason === 'rate' ? t('app.chat.tooFast') : m.reason === 'game' && typeof m.code === 'string' && has(`chat.game.${m.code}`) ? t(`chat.game.${m.code}`) : m.reason === 'game' && typeof m.text === 'string' ? m.text.slice(0, 40) : t('app.chat.notSent'));
  else if (m.t === 'notice') notice.msg(m); // 发版预告 (public/notice.js, office/notice.ts)
  else if (m.t === 'broadcast') { setCaster(m.id); if (m.id && m.id !== me?.id) toast(t('app.cast.on', { name: m.name })); }
}
/** An otter's name as shown in the name tag, chat log and online list (agents, kind "agent" from the server, get the
 *  robot pixel icon next to it there: tagIcons, chatGot, showOnline, greet.js, meeting.js). */
const agentName = (p, name = p.name) => name;
function addPeer(p) {
  const q = { ...p, drawX: p.x, drawY: p.y, emote: null };
  if (p.emote) setEmote(q, p.emote.emote, p.emote.ms); // arrived mid-emote: the rest of it
  peers.set(p.id, q); syncAudio();
}
function setCaster(id) { caster = id; $('cast').classList.toggle('live', id === me?.id); setBar('cast', id === me?.id ? 'live' : 'idle'); }
function toast(text, go, ic, goText = t('app.toast.go')) {
  put($('toastText'), ic, text); $('toast').style.display = 'flex';
  $('toastGo').style.display = go ? '' : 'none'; $('toastGo').textContent = goText; $('toastGo').onclick = () => { go(); $('toast').style.display = 'none'; };
  clearTimeout(toast.t); toast.t = setTimeout(() => ($('toast').style.display = 'none'), 8000);
}
$('gather').onclick = () => send({ t: 'gather' });
// ---- actions (动作, public/emotes.js): keys 1-8, the 动作 menu, a long press on the map; the office shows it to whoever hears me
function playEmote(id) { if (me && !me.moving && !(id === 8 && me.boat)) send({ t: 'emote', emote: id }); } // 睡觉 (8): not in the water
const emotes = emoteMenu({ button: $('emote'), play: playEmote });
// ---- links into Raft (public/raft-links.js): 在 Raft 里私聊 on a card, a 会议室's bound Raft 频道
const raftLinks = initRaftLinks({ send, toast, server: () => (guestMe || visitMe ? null : myServerInfo) }); // a guest has no Raft account: no links
// ---- greetings (public/greet.js): the card of whoever I walk past (top right), 打招呼 / 回招呼
// ---- 请出去 and agent 上限 (public/agent-door.js, office/agentDoor.ts): on an agent's card, its online-list menu, 设置
const agentDoor = initAgentDoor({ send, me: () => me, toast, addSettingsSection, changed: () => { if (onlineLast) showOnline(onlineLast); } });
// ---- 设置 → 管理：测试 agent (public/devbots.js, src/devbots/): deck-dev only, owners and admins; the section shows only when the server says so
if (!guestMe && !visitMe) initDevBots({ addSettingsSection, toast, room: () => floor.room ?? 'main' });
/** A card action that answers on the page (a confirm box): from the 小窗, bring the page to the front first */
const frontFirst = (a) => a && { ...a, go: () => { try { window.focus(); } catch {} a.go?.(); } };
/** 走过去: my otter walks to where p stands (their card, here and in the 小窗) */
function walkTo(p) { return mover.travelTo(Math.floor(p.x + 0.5), Math.floor(p.y + 0.8), false, null); }
const greet = initGreet({ me: () => me, peers: () => peers, hearing: () => hearing, caster: () => caster, zoneAt, send, lookOf, buildSheet, otterImgs, agentName, toast, invite: { forCard: (p) => invite.forCard(p) }, here: guestMe || visitMe ? undefined : { forCard: (p) => meetinv.forCard(p) }, call: { forCard: (p) => callUi?.forCard(p) ?? null }, door: { forCard: (p) => agentDoor.forCard(p) }, talk: { forCard: (p) => audioProvider.isAgora() ? null : secretary.forCard(p) }, raftProfile: (p) => raftLinks.profile(p), status: (p) => statusOf(p), statusText: statusLine, statusIcon: (s) => STATES[s.s].icon, dndNear: DND_NEAR,
  walkTo, unpinned: () => pick.clear() });
// ---- 双人互动 (public/duo.js): two otters close and facing each other get a 互动 prompt (E or a tap): 蹭蹭, 击掌, 转圈圈, 一起漂
const duo = initDuo({ me: () => me, peers: () => peers, send, toast, lookOf, status: (p) => statusOf(p),
  typing: () => { const a = document.activeElement; return !!a && (a.matches?.('input, textarea, select') || a.isContentEditable); },
  eTaken: () => !!whiteboard.near() || !!sit.hint()?.seat });
// ---- my status (public/status.js): 在线 / 离开 / 忙碌 / 请勿打扰, the chip at the start of the bar and 设置 → 我的状态
let pip = null; // the 小窗 (public/pip.js), made further down
let callUi = null; // 通话 (public/call.js), made further down
const presence = initStatus({ signOut: { get label() { return SIGN_OUT.label; }, run: () => signOut() }, present: () => !!pip?.open(), room: floor.room, server: () => myServer, active: () => !!me && (me.moving || speaking(me)), send, me: () => me, peers: () => peers, toast, bar: document.querySelector('.bar'), addSettingsSection, changed: () => { profile?.refresh(); if (onlineLast) showOnline(onlineLast); } });
// ---- 设置 → 声音 (public/sounds.js): greetings, actions near me, invites, chat, the games' sounds
// (onMessage calls it: safe while no top-level await sits between createNet and here, as for presence above)
const officeSounds = initSounds({ me: () => me, peers: () => peers, statusOf, zoneAt, addSettingsSection, off: () => speaker.off() }); // 扬声器 「关闭声音」 silences them too
$('zin').onclick = () => camera.step(1); $('zout').onclick = () => camera.step(-1); // one press, one zoom step (deck/camera.js)
$('cast').onclick = () => send({ t: 'broadcast', on: caster !== me?.id });

// ---- audio: publish my mic once, pull everyone I may hear, volume follows distance
const local = { pc: null, sessionId: null, trackName: null, mic: null, send: null, level: null, ready: false, gen: 0 }; // ready: my mic is published (pulls may start)
// gen: which connection to the SFU this is; voiceLost counts it up. A step started on an older one (pull, pullVideo,
// renegotiate, closeTracks: each takes the pc, session and gen it began with) stops at its next await and touches
// nothing of the new one: the queue is started afresh then, so such a step may still be waiting on a fetch.
const stale = (g) => g !== local.gen;
const gone = () => Object.assign(new Error('voice reconnected meanwhile'), { stale: true });
function analyser(node) { const a = actx.createAnalyser(); a.fftSize = 512; node.connect(a); const buf = new Float32Array(a.fftSize);
  return () => { a.getFloatTimeDomainData(buf); let s = 0; for (const v of buf) s += v * v; return Math.sqrt(s / buf.length); }; }
let actx, queue = Promise.resolve();
let voiceStopped = false; // set when a newer tab took over: voice that was still starting up stops as it lands
const SFU_START_MS = 15000;
let sfuStarting = null, sfuRetryTimer = 0, sfuStartFailures = 0, sfuRepipePending = false;
function closeStartContexts(token) {
  for (const ctx of token?.contexts ?? []) if (ctx.state !== 'closed') try { Promise.resolve(ctx.close()).catch(() => {}); } catch {}
  token?.contexts?.clear();
}
function cancelSfuStart() {
  clearTimeout(sfuRetryTimer); sfuRetryTimer = 0;
  if (sfuStarting) { clearTimeout(sfuStarting.timer); sfuStarting = null; local.gen++; }
  rtc.cancelPublish();
}
function stopVoice() {
  cancelSfuStart();
  closeStartContexts(local.startToken);
  cancelPendingMicSwap();
  audioProvider.stop();
  local.ready = false;
  stopShare(); // a picker or publish may be in progress even though share.mid is not set yet
  try { for (const s of local.pc?.getSenders() || []) s.track?.stop(); local.pc?.close(); } catch {}
  try { local.send?.track?.stop(); } catch {}
  try { if (local.send && !local.send.closed) { local.send.closed = true; Promise.resolve(local.send.close?.()).catch(() => {}); } } catch {}
  try { for (const t of local.mic?.getTracks() || []) t.stop(); if (actx && actx.state !== 'closed') actx.close().catch(() => {}); } catch {}
}
const serial = (f) => (queue = queue.then(f).catch((e) => console.error(e)));
function onRemoteVoiceTrack(e) {
  const all = audible();
  const sp = all.find((q) => q.screenMid && q.screenMid === e.transceiver.mid);
  if (sp) return showScreen(sp, e.track);
  const cp = all.find((q) => q.camMid && q.camMid === e.transceiver.mid);
  if (cp) { cp.camStream = new MediaStream([e.track]); return meeting.refresh(); }
  const p = all.find((q) => q.mid === e.transceiver.mid); if (!p) return;
  const stream = new MediaStream([e.track]);
  p.audio = new Audio(); p.audio.srcObject = stream; p.audio.muted = true; // Chrome needs a sink for WebRTC audio
  const src = actx.createMediaStreamSource(stream); p.gainNode = actx.createGain(); p.gainNode.gain.value = 0;
  src.connect(p.gainNode).connect(actx.destination);
  p.level = analyser(src);
}
const rtc = createSfuAdapter({ local, stale, gone, serial, send, vq,
  room: () => floor.room ?? 'main', server: () => (!guestMe && !visitMe ? myServer : null), serverMoved,
  voiceStopped: () => voiceStopped || audioProvider.isAgora(), onTrack: onRemoteVoiceTrack, watchPc,
  backoff: (...args) => backoff(...args), backedOff: (...args) => backedOff(...args),
  hideScreen: (p) => hideScreen(p), refreshMeeting: () => meeting.refresh(),
  share: () => share, shareScope: () => meeting.shareScope(),
  screenStarted: () => { $('share').classList.add('live'); setBar('share', 'live'); meeting.refresh(); },
  screenStopped: () => { $('share').classList.remove('live'); setBar('share'); meeting.refresh(); },
  screenStalled: () => voiceLost(),
  sessionReset: (sid) => { if (sid && sid === local.sessionId && !audioProvider.isAgora()) voiceLost(); },
});
const { publishMic, pull, unpull, pullVideo, unpullVideo } = rtc;
// SFU starts only after the Office selects it. Agora starts separately with a full grant delivered by the current
// Office socket; a flag, prejoin, URL parameter or locally chosen channel is never a channel authorization.
const audioProvider = createVoiceProvider({
  startSfu: audioStart,
  beforeAgora: () => { stt.suspendForProviderChange(); secretary.end(); stopSfuForAgora(); }, // capture consumers stay disabled for this provider
  createAgora: async () => {
    if (prejoin?.releasing()) { setBar('mic', 'previewWait'); $('mic').classList.remove('on'); }
    await prejoin?.released(); // a cancelled browser permission request may still produce a track; wait until it is stopped
    const { createAgoraPageProvider } = await import('/rtc/agora-page.ts');
    const ctx = new AudioContext(); keepRunning(ctx);
    return createAgoraPageProvider({ audioContext: ctx, muted: () => muted, voiceMode, talking: () => voice.talking(),
      device: micDevice, denoise: denoiseWanted, denoiseLevel: agoraDenoiseLevel, speaker: () => speaker.gain(),
      peerGain: (id) => { const p = audible().find((x) => x.id === id); return p ? loudness(id, p) * (p.duckUntil > performance.now() ? 0.1 : 1) : 0; },
      onReady: () => { voice.release(); actx = ctx; local.ready = true; setBar('mic', muted ? 'off' : 'on'); $('mic').classList.toggle('on', !muted);
        if (audioProvider.sttAvailable() && sttWelcome) { stt.resumeForProviderChange(); stt.welcome(sttWelcome); if (sttNeed) stt.need(sttNeed); stt.repipe(); }
        if (ctx.state === 'suspended') toast(t('app.audio.tapToHear')); },
      onCaptureChange: () => stt.repipe(),
      onTokenWillExpire: () => voiceAuthorization.refresh(),
      onDown: agoraDown, onDenoiserUnavailable: () => toast(t('app.mic.noDenoise')) });
  },
});
// Cut local SFU media immediately when an authorized replacement arrives. The Office/provider must deal with
// previously issued track credentials and their revocation; closing this browser's PC is not a server-side fence.
function stopSfuForAgora() {
  voice.release();
  cancelSfuStart();
  closeStartContexts(local.startToken);
  sfuRepipePending = false;
  local.gen++; local.ready = false; clearTimeout(lostTimer); rejoining = false; queue = Promise.resolve();
  stopShare(); // cancels a pending picker/publish too, preserving main's screen-scope cleanup
  if (meeting.info?.()?.cam?.on) send({ t: 'cam', trackName: null });
  meeting.stop();
  try { for (const sender of local.pc?.getSenders() || []) sender.track?.stop(); local.pc?.close(); } catch {}
  try { local.send?.track?.stop(); } catch {}
  try { if (local.send && !local.send.closed) { local.send.closed = true; Promise.resolve(local.send.close?.()).catch(() => {}); } } catch {}
  try { for (const track of local.mic?.getTracks() || []) track.stop(); } catch {}
  cancelPendingMicSwap();
  for (const p of audible()) {
    try { p.gainNode?.disconnect(); p.audio?.pause(); if (p.audio) p.audio.srcObject = null; } catch {}
    try { p.clip?.gain?.disconnect(); } catch {} p.clip = null; p.clips = null;
    p.gainNode = null; p.level = null; p.mid = null; p.pulling = false; p.closing = false;
    if (p.screenMid) hideScreen(p);
    if (p.camMid) p.camStream = null;
    Object.assign(p, { screenMid: null, screenPulled: null, screenBusy: false, camMid: null, camPulled: null, camBusy: false });
  }
  Object.assign(local, { pc: null, sessionId: null, trackName: null, sender: null, mic: null, send: null, level: null, startToken: null });
  if (actx && actx.state !== 'closed') actx.close().catch(() => {});
  actx = null;
}
function agoraDown(reason) {
  if (!audioProvider.isAgora()) return;
  voiceAuthorization.connectionDown(reason);
}
let unsupportedNoticeAt = 0, agoraMicNotice = null, floorNoticeAt = 0;
function retryAgoraMic() {
  if (voiceStopped) return;
  if (voiceAuthorization.retryMicrophone()) { agoraMicNotice = null; setBar('mic', 'reconnecting'); }
}
function agoraUnavailable(reason) {
  if (!audioProvider.isAgora()) return;
  stt.suspendForProviderChange(); sttNeed = null; sttCaptureWanted = false;
  audioProvider.offline(); local.ready = false;
  if (actx && actx.state !== 'closed') actx.close().catch(() => {});
  if (reason === 'mic-denied' || reason === 'mic-missing') {
    setBar('mic', reason === 'mic-denied' ? 'denied' : 'none'); $('mic').classList.remove('on');
    if (agoraMicNotice !== reason) {
      agoraMicNotice = reason;
      toast(t(reason === 'mic-denied' ? 'app.voice.micDenied' : 'app.voice.micMissing'), retryAgoraMic, 'mic_off', t('app.action.retry'));
    }
    return;
  }
  agoraMicNotice = null;
  setBar('mic', 'down'); $('mic').classList.remove('on');
  if (reason === 'unsupported' && Date.now() - unsupportedNoticeAt > 30000) { unsupportedNoticeAt = Date.now(); toast(t('app.voice.agoraUnsupported')); }
  if ((reason === 'recover' || reason === 'reauth') && Date.now() - floorNoticeAt > 30000) {
    floorNoticeAt = Date.now();
    if (reason === 'recover') toast(t('app.voice.agoraRecover'), () => { voiceFloor?.recover(); voiceAuthorization.refresh(); }, 'mic_off', t('app.action.retry'));
    else toast(t('app.voice.agoraReauth'), () => { const url = new URL('/auth/login', location.href), floor = new URL(location.href).searchParams.get('floor'); if (floor) url.searchParams.set('floor', floor); if (myServer) url.searchParams.set('server', myServer); location.assign(url); }, 'mic_off', t('app.voice.agoraSignIn'));
  }
}
let sfuVoiceStarted = false;
voiceFloor = createAgoraFloor({ send, scope: () => myServer && me?.gid && !guestMe && !visitMe ? { server: myServer, subject: me.gid } : null });
const voiceAuthorization = createAgoraAuthorization({
  enterSfu: () => {
    if (voiceStopped) return;
    if (audioProvider.isAgora()) { audioProvider.selectSfu(); stt.resumeForProviderChange(); sfuVoiceStarted = false; }
    if (!sfuVoiceStarted) { sfuVoiceStarted = true; voiceUp(); }
  },
  enterAgoraWait: () => {
    if (voiceStopped) return;
    audioProvider.prepareAgora(); sfuVoiceStarted = false; setBar('mic', 'reconnecting'); $('mic').classList.remove('on');
  },
  unavailable: agoraUnavailable,
  sendRefresh: () => { if (!voiceStopped) send({ t: 'agora_refresh', ...voiceFloor.request() }); },
  grant: (grant) => audioProvider.selectAuthorizedAgora(grant),
  renew: (id, token, expiresAt) => audioProvider.renewAuthorizedAgora(id, token, expiresAt),
  roster: (id, version, uids, peers) => audioProvider.updateAuthorizedPeers(id, version, uids, peers),
});
// Start voice; if it can't: 语音暂时连不上. The office refusing new SFU sessions for a while (too many: routes/sfu.ts,
// 429) is tried again in 2 minutes, without a reload.
function retrySfuStart(ms) {
  clearTimeout(sfuRetryTimer);
  sfuRetryTimer = setTimeout(() => { sfuRetryTimer = 0; if (!voiceStopped && !audioProvider.isAgora()) voiceUp(); }, ms);
}
function repipeSfuCapture() {
  try { stt.repipe(); } catch (e) { console.warn('voice: transcription update failed', e); }
  try { secretary.repipe(); } catch (e) { console.warn('voice: secretary update failed', e); }
}
function restartSfuStart(attempt) {
  if (sfuStarting !== attempt || voiceStopped || audioProvider.isAgora()) return;
  stopVoice(); rejoining = false; queue = Promise.resolve();
  Object.assign(local, { pc: null, sessionId: null, trackName: null, sender: null, mic: null, send: null, level: null, startToken: null });
  sfuRepipePending = true; repipeSfuCapture();
  setBar('mic', 'reconnecting'); $('mic').classList.remove('on');
  retrySfuStart(Math.min(30000, 1000 * 2 ** Math.min(sfuStartFailures++, 5)));
}
function armSfuStart(g) {
  const attempt = sfuStarting;
  if (attempt && attempt.gen === g && !attempt.timer) attempt.timer = setTimeout(() => restartSfuStart(attempt), SFU_START_MS);
}
function voiceUp() {
  if (voiceStopped || audioProvider.isAgora() || sfuStarting || local.ready) return;
  clearTimeout(sfuRetryTimer); sfuRetryTimer = 0;
  const attempt = (sfuStarting = { gen: local.gen, timer: 0 });
  audioProvider.start().then(() => {
    if (sfuStarting !== attempt) return;
    clearTimeout(attempt.timer); sfuStarting = null;
    if (local.ready) sfuStartFailures = 0;
  }).catch((e) => {
    if (sfuStarting !== attempt || voiceStopped || stale(attempt.gen) || audioProvider.isAgora()) return;
    console.error(e);
    const rate = e?.status === 429, capacity = e?.code === 'E_SFU_CAPACITY', rebuild = e?.code === 'E_SFU_REBUILD' || capacity;
    const retry = rate || rebuild || !e?.status || e.status >= 500;
    stopVoice(); Object.assign(local, { pc: null, sessionId: null, trackName: null, sender: null, mic: null, send: null, level: null, startToken: null });
    sfuRepipePending = retry; repipeSfuCapture();
    setBar('mic', rate ? 'busy' : retry ? 'reconnecting' : 'down'); $('mic').classList.remove('on');
    if (retry) retrySfuStart(rate ? 120000 : capacity ? 5000 : Math.min(30000, 1000 * 2 ** Math.min(sfuStartFailures++, 5)));
  });
}
/** The office refused something ({t:"error"}). My mic's announcement refused (the office doesn't know my SFU session:
 *  nobody there can pull it) used to go unseen; now the mic button says voice isn't connected. */
function officeError(m) {
  console.warn('office refused:', m.code, m.message);
  // a message over the office's size limit (Holt's Room.maxFrame; re: its type): the map editor's save and the
  // whiteboard's changes say so where they were made (the save stays a draft; the board reloads)
  if (m.code === 'E_TOO_BIG' && m.re === 'map_save') mapEditor.message(m);
  if (m.code === 'E_TOO_BIG' && m.re === 'wb_op') whiteboard.msg({ t: 'wb_err', reason: 'big' });
  if (m.code === 'E_FORBIDDEN' && m.message === 'not your SFU session' && !voiceStopped) { setBar('mic', 'down'); $('mic').classList.remove('on'); }
}
async function audioStart() {
  const g = local.gen;
  if (prejoin?.releasing()) { setBar('mic', 'previewWait'); $('mic').classList.remove('on'); }
  await prejoin?.released(); // boarding is not blocked, but no second microphone opens before the preview is released
  if (voiceStopped || stale(g) || audioProvider.isAgora()) return;
  // made after awaits (the map loading, a 发版预告 reload with no tap at all): on an iPhone it starts suspended and
  // everyone I pull plays into silence until a tap resumes it (volumes() asking every frame doesn't count there)
  const ctx = new AudioContext(), startToken = { contexts: new Set() }; local.startToken = startToken; actx = ctx; keepRunning(ctx);
  const lost = () => voiceStopped || stale(g) || audioProvider.isAgora();
  let mic = null, pipeline = null;
  const discard = () => {
    closeStartContexts(startToken);
    try { pipeline?.track?.stop(); } catch {}
    try { if (pipeline && !pipeline.closed) { pipeline.closed = true; Promise.resolve(pipeline.close?.()).catch(() => {}); } } catch {}
    try { for (const track of mic?.getTracks() || []) track.stop(); } catch {}
    if (ctx.state !== 'closed') ctx.close().catch(() => {});
    // Do not close or clear a newer provider or a newer SFU start that replaced this attempt.
    if (local.startToken === startToken) {
      local.startToken = null;
      if (!audioProvider.isAgora() && !stale(g)) {
        try { local.pc?.close(); } catch {}
        Object.assign(local, { pc: null, sessionId: null, trackName: null, sender: null, ready: false });
      }
      if (local.mic === mic) local.mic = null;
      if (local.send === pipeline) { local.send = null; local.level = null; }
      if (actx === ctx) actx = null;
    }
  };
  try {
    const asked = micConstraints();
    try {
      mic = await navigator.mediaDevices.getUserMedia(asked);
    } catch { if (!lost()) setBar('mic', 'denied'); discard(); return; }
    if (lost()) { discard(); return; }
    if (muted) for (const track of mic.getAudioTracks()) track.enabled = false;
    local.mic = mic;
    // Permission prompts may remain open indefinitely; start the deadline only once a real mic was obtained.
    armSfuStart(g);
    const agcAsked = asked.audio.autoGainControl;
    // Send (and meter) the denoised, gated signal. If the pipeline can't be built, the raw mic, with 按住说话 done by
    // switching the track on and off (never an open mic by surprise), and a note that 降噪 is off.
    pipeline = await micPipeline(mic, { agcAsked, onContext: (next) => {
      startToken.contexts.add(next);
      if (lost() || local.startToken !== startToken) closeStartContexts(startToken);
    } }).catch((e) => { console.error('denoise off:', e); return null; });
    if (lost()) { discard(); return; }
    if (!pipeline) {
      closeStartContexts(startToken);
      let level = () => 0; try { level = analyser(ctx.createMediaStreamSource(mic)); } catch {}
      pipeline = rawMic(mic, { level, muted, agcAsked });
      toast(t('app.mic.noDenoise'));
    }
    local.send = pipeline;
    startToken.contexts.clear(); // the adopted pipeline owns and closes its own context
    local.level = local.send.level;
    if (muted) setMute(true); // (came muted from another boat: the pipeline starts open)
    local.send.setTalk(voiceMode() === 'open' || voice.talking(), true);
    if (lost()) { discard(); return; }
    if (!(await publishMic()) || lost()) { discard(); return; }
    watchMic();
    remic(); // 设置 → 语音 changed while voice was coming up
    // pulls wait for this (pull, pullVideo): the people already talking when I came in are pulled now
    local.ready = true; syncAudio();
    if (sfuRepipePending) { sfuRepipePending = false; repipeSfuCapture(); }
    if (local.pc?.connectionState === 'failed') voiceLost(); // (it failed while coming up)
    // still suspended (an iPhone that hasn't been tapped since): say what brings the sound on
    if (ctx.state === 'suspended') toast(t('app.audio.tapToHear'));
  } catch (error) { discard(); throw error; }
}
// ---- my own connection to the SFU lost (the laptop slept, the network changed): Cloudflare keeps a session that lost
// connectivity only 30 s, and nothing else would ever bring it back: everyone would go on pulling a dead track (and I
// hear nobody). Failed, or disconnected for LOST_MS: a new session, my mic published again and announced (the others'
// pages pull the new track: the "track" handler), and everyone I hear pulled again on it. If that fails (still
// offline), tried again after a growing wait (RETRY_MS).
const LOST_MS = 10000;
let lostTimer = 0, rejoining = false, rejoinFails = 0, mediaOff = false;
function watchPc(pc) {
  pc.addEventListener('connectionstatechange', () => {
    if (pc !== local.pc) return;
    clearTimeout(lostTimer);
    if (pc.connectionState === 'failed') voiceLost();
    else if (pc.connectionState === 'disconnected') lostTimer = setTimeout(() => { if (pc === local.pc && pc.connectionState === 'disconnected') voiceLost(); }, LOST_MS);
  });
}
function voiceLost() {
  if (voiceStopped || audioProvider.isAgora()) return;
  if (!local.ready) { if (sfuStarting) restartSfuStart(sfuStarting); return; }
  if (rejoining) return;
  // A failure can arrive just after audioStart marked ready but before its promise settled.
  if (sfuStarting) { clearTimeout(sfuStarting.timer); sfuStarting = null; }
  console.warn('voice: my connection to the SFU is', local.pc?.connectionState, '- publishing again');
  rejoining = true; local.ready = false; local.gen++;
  cancelPendingMicSwap();
  setBar('mic', 'reconnecting');
  const old = local.pc; local.pc = null; local.sessionId = null; local.trackName = null; local.sender = null;
  try { old?.close(); } catch {}
  // a step still waiting on that connection never settles once it's closed (WebRTC leaves its promise pending): the
  // SFU calls start a new line, or they'd all wait behind it for good
  queue = Promise.resolve();
  // what I pulled was on that connection: gone with it (syncAudio pulls everyone again once I'm back)
  for (const p of audible()) {
    p.gainNode?.disconnect(); p.gainNode = null; p.level = null; p.mid = null; p.pulling = false; p.closing = false; backedOff(p, 'pull');
    p.screenBusy = false; p.camBusy = false;
    if (p.screenMid) hideScreen(p);
    if (p.camMid) p.camStream = null;
    Object.assign(p, { screenMid: null, screenPulled: null, camMid: null, camPulled: null });
  }
  meeting.refresh();
  // my screen and camera were on it too: off (with local.pc gone, their close is only the "screen"/"cam" null), and
  // said so when voice is back (rejoin)
  mediaOff = !!share.mid || !!meeting.info?.()?.cam?.on;
  if (share.mid || share.pending) stopShare();
  meeting.camDropped();
  rejoin();
}
function rejoin() {
  const g = local.gen;
  serial(async () => {
    if (voiceStopped || audioProvider.isAgora() || stale(g) || !rejoining || sfuStarting) return;
    const attempt = (sfuStarting = { gen: g, timer: 0 }); armSfuStart(g);
    try {
      const published = await publishMic();
      if (sfuStarting !== attempt || stale(g) || voiceStopped || audioProvider.isAgora()) return;
      if (!published) return stopVoice();
      if (local.pc.connectionState === 'failed') throw new Error('the new connection failed too');
      rejoinFails = 0; rejoining = false; local.ready = true; syncAudio();
      setBar('mic', muted ? 'off' : 'on');
      toast(t(mediaOff ? 'app.voice.backMediaOff' : 'app.voice.back')); mediaOff = false;
      // the mic: a 设置 → 语音 change, or a mic unplugged / switched while I was offline, went unseen (no sender then):
      // take it as it should be now (remic: other processing; micChanged: swapMic if the mic moved or its track ended)
      remic(); micChanged();
    } catch (e) { // (still offline?) again after a while, with a new connection
      if (sfuStarting !== attempt || stale(g) || voiceStopped || audioProvider.isAgora()) return;
      console.error('voice: publishing again failed:', e);
      try { local.pc?.close(); } catch {}
      local.pc = null; local.sessionId = null; local.trackName = null;
      sfuRetryTimer = setTimeout(rejoin, RETRY_MS[Math.min(rejoinFails++, RETRY_MS.length - 1)]);
    } finally { if (sfuStarting === attempt) { clearTimeout(attempt.timer); sfuStarting = null; } }
  });
}
// ---- the mic changing under us: unplugged, or the system switched to another one (a headset plugged in). The browser
// doesn't follow either way (an unplugged mic's track just ends, and the others hear silence for good), so take the
// mic again, build a new send pipeline, and swap it into the published track (replaceTrack: no renegotiation).
let micSwapping = false, micSwapAgain = false, micSwapReconfigure = false, micSwapToken = 0, micTimer = 0, swappingMicStream = null, swappingMicPipeline = null, swappingMicContext = null;
function cancelPendingMicSwap() {
  micSwapToken++; micSwapping = false; micSwapAgain = false; micSwapReconfigure = false; clearTimeout(micTimer); micTimer = 0;
  const stream = swappingMicStream, pipeline = swappingMicPipeline, ctx = swappingMicContext;
  swappingMicStream = null; swappingMicPipeline = null; swappingMicContext = null;
  if (pipeline && pipeline !== local.send) {
    try { pipeline.track?.stop(); } catch {}
    try { if (!pipeline.closed) { pipeline.closed = true; Promise.resolve(pipeline.close?.()).catch(() => {}); } } catch {}
  }
  if (ctx && ctx !== local.send?.ctx && ctx.state !== 'closed') try { Promise.resolve(ctx.close()).catch(() => {}); } catch {}
  if (stream && stream !== local.mic) try { for (const track of stream.getTracks()) track.stop(); } catch {}
}
function watchMic() {
  local.mic?.getAudioTracks()[0]?.addEventListener('ended', () => micChanged());
  if (!watchMic.on) { watchMic.on = true; navigator.mediaDevices.addEventListener?.('devicechange', () => micChanged()); }
}
function micChanged() { clearTimeout(micTimer); micTimer = setTimeout(() => swapMic().catch((e) => console.error('mic swap:', e)), 300); }
/** Is the mic we send gone, not the one picked (micDevice), or (none picked) no longer the system's default? (Chrome lists the default as deviceId "default", with
 *  the group of the device it stands for.) */
async function micMoved() {
  const tr = local.mic?.getAudioTracks()[0]; if (!tr) return false;
  if (tr.readyState === 'ended') return true;
  const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput'), now = tr.getSettings();
  // a mic picked on the pre-join page or in 设置 → 语音 (denoise.js micDevice): follow it while it's plugged in
  const want = micDevice();
  if (want && list.some((d) => d.deviceId === want)) return now.deviceId !== want;
  if (now.deviceId && !list.some((d) => d.deviceId === now.deviceId || d.deviceId === 'default')) return true;
  const def = list.find((d) => d.deviceId === 'default');
  return !!(def && now.groupId && def.groupId && def.groupId !== now.groupId);
}
/** 设置 → 语音 changed what the mic must be taken with (人声隔离 wants the browser's gain control off, 标准降噪 on). */
// Compares with what the mic was asked for (kept by the pipeline), never what the browser reports back (Safari doesn't
// list autoGainControl), so a swap that got what it asked for can't trigger another.
function remic() { if (local.send && local.sender && local.send.agcAsked !== micConstraints().audio.autoGainControl) swapMic(true).catch((e) => console.error('mic swap:', e)); }
async function swapMic(force = false) {
  const g = local.gen, sender = local.sender, staleRequest = () => voiceStopped || stale(g) || audioProvider.isAgora() || sender !== local.sender;
  if (staleRequest() || !sender) return;
  if (micSwapping) { micSwapAgain = true; micSwapReconfigure ||= force; return; }
  const token = ++micSwapToken, lost = () => staleRequest() || token !== micSwapToken;
  micSwapping = true; let swapped = false, fellBack = false;
  try {
    if (!(force || (await micMoved())) || lost()) return;
    // forced (other processing for the same mic): let go of it first, Chrome keeps one set of processing per device;
    // if the mic then can't be had with the new processing, take it again as it was
    let asked = micConstraints();
    if (force) for (const t of local.mic?.getTracks() || []) t.stop();
    let mic = await navigator.mediaDevices.getUserMedia(asked).catch(() => null);
    if (!mic && force && !lost()) { fellBack = true; asked = { audio: { ...asked.audio, autoGainControl: local.send.agcAsked } }; mic = await navigator.mediaDevices.getUserMedia(asked).catch(() => null); }
    if (!mic) { if (!lost()) setBar('mic', 'lost'); return; }
    if (lost()) { for (const t of mic.getTracks()) t.stop(); return; }
    swappingMicStream = mic;
    if (muted) for (const track of mic.getAudioTracks()) track.enabled = false;
    const agcAsked = asked.audio.autoGainControl;
    let next = await micPipeline(mic, { agcAsked, onContext: (ctx) => {
      if (lost()) { try { Promise.resolve(ctx.close()).catch(() => {}); } catch {} }
      else swappingMicContext = ctx;
    } }).catch(() => null);
    if (lost()) {
      try { next?.track?.stop(); } catch {}
      try { Promise.resolve(next?.close?.()).catch(() => {}); } catch {}
      for (const t of mic.getTracks()) t.stop(); return;
    }
    if (!next) {
      if (swappingMicContext?.state !== 'closed') try { Promise.resolve(swappingMicContext?.close()).catch(() => {}); } catch {}
      swappingMicContext = null;
      let level = () => 0; try { level = analyser(actx.createMediaStreamSource(mic)); } catch {} next = rawMic(mic, { level, muted, agcAsked });
    }
    swappingMicPipeline = next;
    if (next.raw) next.setMuted(muted); else mic.getAudioTracks()[0].enabled = !muted;
    // replaceTrack may take effect before its promise resolves. Keep the candidate silent until it is adopted.
    next.track.enabled = false;
    try { await sender.replaceTrack(next.track); }
    catch (e) {
      // The candidate never became ours: release both the pipeline output and its input device.
      const oldTracks = new Set(local.mic?.getTracks() || []);
      try { if (next.track !== local.send?.track && !oldTracks.has(next.track)) next.track?.stop(); } catch {}
      try { next.closed = true; Promise.resolve(next.close?.()).catch(() => {}); } catch {}
      for (const t of mic.getTracks()) if (!oldTracks.has(t)) try { t.stop(); } catch {}
      if (!lost() && !local.mic?.getAudioTracks().some((t) => t.readyState === 'live')) setBar('mic', 'lost');
      throw e;
    }
    if (lost()) {
      try { next.track?.stop(); } catch {}
      try { Promise.resolve(next.close?.()).catch(() => {}); } catch {}
      for (const t of mic.getTracks()) t.stop(); return;
    }
    // mute and 按住说话 as they are now (they may have changed during the awaits; they acted on the old pipeline)
    const talking = voiceMode() === 'open' || voice.talking();
    if (next.raw) { next.setMuted(true); next.setTalk(talking); next.setMuted(muted); }
    else { mic.getAudioTracks()[0].enabled = !muted; next.setTalk(talking, true); next.track.enabled = !muted; }
    const old = { mic: local.mic, send: local.send };
    local.mic = mic; local.send = next; local.level = next.level;
    secretary.repipe(); // fence the old secretary tap before the old track and graph are closed
    try { old.send.closed = true; Promise.resolve(old.send.close?.()).catch(() => {}); } catch {}
    for (const t of old.mic?.getTracks() || []) t.stop();
    setBar('mic', muted ? 'off' : 'on');
    if (!force) toast(t('app.mic.swapped'));
    watchMic(); stt.repipe(); // 语音转文字 taps the new pipeline now
    swapped = true;
  } finally {
    if (token === micSwapToken) {
      const again = micSwapAgain && !lost();
      const reconfigure = micSwapReconfigure && !lost();
      micSwapAgain = false; micSwapReconfigure = false; swappingMicStream = null; swappingMicPipeline = null; swappingMicContext = null; micSwapping = false;
      if (again) micChanged();
      if (reconfigure && (!swapped || fellBack)) queueMicrotask(() => { if (!lost()) remic(); });
    }
  }
  if (swapped && !fellBack) remic(); // changed while this swap ran (not after falling back: it would only fail again)
}
let muted = !!voyage?.muted; // muted on the boat I came from (换船): still muted here
/** Muted: the mic track sends silence, and the others see 🔇 on my name. Button or M. */
function setMute(on) {
  if (audioProvider.isAgora()) {
    if (!audioProvider.microphoneReady()) return;
    muted = on; audioProvider.setMuted(on);
  } else {
    const tr = local.mic?.getAudioTracks()[0]; if (!tr) return;
    muted = on;
    if (local.send?.raw) local.send.setMuted(on);
    else { tr.enabled = !on; if (local.send?.track) local.send.track.enabled = !on; }
  }
  // A second input may already be open while replaceTrack is pending; mute it too until adoption.
  if (on) for (const track of swappingMicStream?.getAudioTracks() || []) track.enabled = false;
  secretary.muteChanged(on);
  if (me) me.muted = on;
  $('mic').classList.toggle('on', !on); setBar('mic', on ? 'off' : 'on');
  send({ t: 'mute', on });
}
$('mic').onclick = () => {
  if (audioProvider.isAgora() && voiceAuthorization.microphoneBlocked()) return retryAgoraMic();
  setMute(!muted);
};
const voice = mountVoiceSettings({ after: $('mic'), getPipeline: () => audioProvider.isAgora() ? audioProvider.settings() : local.send,
  getMicrophoneReady: () => audioProvider.isAgora() ? audioProvider.microphoneReady() : !!local.ready && local.mic?.getAudioTracks().some((tr) => tr.readyState === 'live'), addSettingsSection,
  deviceChanged: (id) => audioProvider.isAgora() ? (voiceAuthorization.microphoneBlocked() ? retryAgoraMic() : audioProvider.setDevice(id)) : micChanged(),
  remic: () => { if (!audioProvider.isAgora()) remic(); } }); // 设置 → 语音 (降噪, 按住说话) + Space / hold button
/** I'm a living player of the spy hut's running game, in the hut: the office lets nobody outside it reach me (voice.ts
 *  sealed()), call members on other floors included (set once the games are up: games.sealed). */
let hutSealed = () => false;
/** Everyone whose voice I may get: the people on this floor, and my call's members on other floors (public/call.js). */
const audible = () => [...peers.values(), ...(callUi?.remote().values() ?? []), ...nb.ghosts.values()];
// A pull that failed (refused by the office or the SFU, a renegotiation that failed) is tried again after a wait that
// grows each time in a row: 3, 6, 12, then every 30 s. Per kind (k: 'pull' the mic, 'screen', 'cam') and per track: a
// new track (or session) of theirs starts over at once, and so does a pull that worked.
const RETRY_MS = [3000, 6000, 12000, 30000];
const srcOf = (p, k) => `${p.sessionId}/${k === 'pull' ? p.trackName : p[k + 'Track']}`;
function backoff(p, k, flat = false) {
  // flat: the office said no (403: not in earshot, not in my call yet): that changes with the next "hear" or call
  // update, and wanting them again starts over anyway (wanted()), so it stays at the first wait
  const src = srcOf(p, k), n = flat ? 0 : p[k + 'Src'] === src ? p[k + 'Fails'] ?? 0 : 0;
  Object.assign(p, { [k + 'Src']: src, [k + 'Fails']: n + 1, [k + 'At']: Date.now() + RETRY_MS[Math.min(n, RETRY_MS.length - 1)] });
}
const backedOff = (p, k) => Object.assign(p, { [k + 'Fails']: 0, [k + 'At']: 0 });
/** Is p's track (k) wanted now? Wanted again after not being (back in earshot, into a call with them): its failures so
 *  far no longer count, it's pulled at once. */
function wanted(p, k, want) {
  if (want && !p[k + 'Wanted']) backedOff(p, k);
  p[k + 'Wanted'] = want; return want;
}
/** May p's track be pulled now: not waiting after a failed pull of that same track? */
const due = (p, k, now) => !(p[k + 'At'] > now && p[k + 'Src'] === srcOf(p, k));
/** Stop pulling someone's tracks (they left, or a call member on another floor is gone from the call). */
function dropMedia(p) { if (p?.mid) serial(() => unpull(p)); if (p?.screenMid) serial(() => unpullVideo(p, 'screen')); if (p?.camMid) serial(() => unpullVideo(p, 'cam')); }
function syncAudio() {
  if (audioProvider.isAgora()) { audioProvider.sync(); return; } // no SFU audio/video pulls in an Agora session
  const now = Date.now(), room = meeting.room();
  // my call's members on other floors: their mic always, their screen and (a few) cameras as for anyone in the call
  for (const p of callUi?.remote().values() ?? []) {
    const want = wanted(p, 'pull', !p.gone && !!p.trackName && !hutSealed());
    if (want) p.lastWanted = now;
    // (refused: the office doesn't have them in my call yet, or any more: ask again in a few seconds, not every second)
    if (want && !p.pulling && due(p, 'pull', now)) { p.pulling = true; serial(() => pull(p)); } // (pull: a refused one waits a few seconds)
    if (!want && p.mid && !p.closing) { p.closing = true; serial(() => unpull(p)); }
    const wantScreen = wanted(p, 'screen', !p.gone && !!p.screenTrack);
    if (wantScreen && p.screenPulled !== p.screenTrack && !p.screenBusy && due(p, 'screen', now)) { p.screenBusy = true; serial(() => pullVideo(p, 'screen')); }
    if (!wantScreen && p.screenMid && !p.screenBusy) { p.screenBusy = true; serial(() => unpullVideo(p, 'screen')); }
    const wantCam = wanted(p, 'cam', !p.gone && !!p.camTrack && meeting.camAllowed(p.id));
    if (wantCam && p.camPulled !== p.camTrack && !p.camBusy && due(p, 'cam', now)) { p.camBusy = true; serial(() => pullVideo(p, 'cam')); }
    if (!wantCam && p.camMid && !p.camBusy) { p.camBusy = true; serial(() => unpullVideo(p, 'cam')); }
  }
  // 邻船: the people at the other boat's edge I hear (the office says so, edge.ts): their mic, as for anyone here
  for (const [id, p] of nb.ghosts) {
    const want = wanted(p, 'pull', hearing.has(id));
    if (want) p.lastWanted = now;
    if (want && p.trackName && !p.pulling && due(p, 'pull', now)) { p.pulling = true; serial(() => pull(p)); }
    if (!want && p.mid && now - (p.lastWanted || 0) > 3000 && !p.closing) { p.closing = true; serial(() => unpull(p)); }
  }
  for (const [id, p] of peers) {
    const want = wanted(p, 'pull', hearing.has(id)); // (the 📢 caster too: the office leaves them out where a hut game says so)
    if (want) p.lastWanted = now;
    if (want && p.trackName && !p.pulling && due(p, 'pull', now)) { p.pulling = true; serial(() => pull(p)); }
    // Out of earshot for a while: stop receiving their audio at all, not just turn it down.
    if (!want && p.mid && now - (p.lastWanted || 0) > 3000 && !p.closing) { p.closing = true; serial(() => unpull(p)); }
    // Screens: shown to whoever hears the sharer, gone as soon as they don't or the share stops.
    const wantScreen = wanted(p, 'screen', hearing.has(id) && !!p.screenTrack);
    if (wantScreen && p.screenPulled !== p.screenTrack && !p.screenBusy && due(p, 'screen', now)) { p.screenBusy = true; serial(() => pullVideo(p, 'screen')); }
    if (!wantScreen && p.screenMid && !p.screenBusy) { p.screenBusy = true; serial(() => unpullVideo(p, 'screen')); }
    // Cameras (会议室 only): pulled only from people in the same meeting room as me, and only while we both are.
    // At most a few at once (meeting.camAllowed: the latest speakers, or pinned); the others show their otter.
    // In a 通话: the cameras of the call's people on this floor (the office tells me them, voice.ts camsTo).
    const wantCam = wanted(p, 'cam', !!p.camTrack && hearing.has(id) && (meeting.inCall() ? !!callUi?.has(id) : !!room && zoneAt(p.x, p.y)?.id === room) && meeting.camAllowed(id));
    if (wantCam && p.camPulled !== p.camTrack && !p.camBusy && due(p, 'cam', now)) { p.camBusy = true; serial(() => pullVideo(p, 'cam')); }
    if (!wantCam && p.camMid && !p.camBusy) { p.camBusy = true; serial(() => unpullVideo(p, 'cam')); }
  }
}
setInterval(syncAudio, 1000);
function showScreen(p, track) {
  hideScreen(p);
  const box = document.createElement('div'); box.className = 'scr'; box.id = 'scr-' + p.id;
  const v = document.createElement('video'); v.autoplay = true; v.playsInline = true; v.muted = true; v.srcObject = p.screenStream = new MediaStream([track]);
  const who = document.createElement('div'); who.className = 'who'; tr(who, 'app.screen.whose', { name: p.name });
  const big = document.createElement('div'); big.className = 'big'; tr(big, 'app.screen.fullscreen'); big.onclick = () => v.requestFullscreen?.();
  box.append(v, who, big); $('screens').appendChild(box);
  meeting.refresh(); // in a 会议室 the screen takes the meeting's stage instead
}
function hideScreen(p) { document.getElementById('scr-' + p.id)?.remove(); if (p.screenStream) { p.screenStream = null; meeting.refresh(); } }
// My own share: the UI chooses an authorized room/call; the SFU adapter owns capture, negotiation and cleanup.
const share = { mid: null, stream: null, pending: null, active: null };
function enforceShareScope() { rtc.enforceScreenScope(); }
function stopShare() { rtc.stopScreen(); }
$('share').onclick = () => {
  if (share.mid || share.pending) return stopShare();
  if (audioProvider.isAgora()) { toast(t('app.voice.videoUnavailable')); return; }
  const scope = meeting.shareScope();
  if (scope == null) { toast(t('app.share.needRoom')); return; }
  if (!local.ready || !local.pc || !local.sessionId) { toast(t('app.share.noVoice')); return; }
  rtc.startScreen(scope);
};
/** In a 通话, someone outside it that I still hear (right next to me) is this loud. */
const CALL_SIDE = 0.45;
/** How loud someone is to me: full in the same room, at the same meeting table (its voice circle) or broadcasting,
 *  fading with distance on the open deck, and a little quieter (DESK_HUSH) when either of us is working at a desk. */
function loudness(id, p) {
  // 🔇 as the office says it (their own mute, or the 主持人's: office/meethost.ts): nothing from them until they unmute,
  // whatever their page does (a stale or changed page that kept its mic open after 主持人把你静音了 stays unheard). Not in
  // the spy hut, where a game keeps a player's 🔇 frozen for the others (office/voice.ts frozen) while they may talk.
  if (p.muted && zoneAt(p.x, p.y)?.id !== 'spy') return 0;
  if (id === caster) return hearing.has(id) ? 1 : 0;
  if (p.remote) return p.gone || hutSealed() ? 0 : 1; // a call member on another floor (not while a hut game seals me in)
  if (!hearing.has(id) || !me) return 0;
  // 通话: the call at full volume; someone outside it (next to me, or I'm next to someone in a call) quieter
  if (callUi?.has(id)) return 1;
  if ((callUi?.active()?.members.length ?? 0) > 1) return CALL_SIDE;
  if (zoneAt(me.x, me.y)) return 1;
  const circle = circleOf(MAP, me); if (circle && circle === circleOf(MAP, p)) return 1;
  const v = Math.max(0.25, Math.min(1, 1 - (Math.hypot(p.x - me.x, p.y - me.y) - 2) / 4.5));
  return atDesk(MAP, me) || atDesk(MAP, p) ? v * DESK_HUSH : v;
}
// ---- 扬声器: how loud everyone is to me overall (0..1) and 关闭声音 (I hear nobody; my mic is untouched). Kept on this
// device; set from 设置 → 扬声器 or the 小窗 (public/pip.js).
const SPEAKER_KEY = 'deck.speaker';
const speaker = (() => {
  let v = 1, off = false;
  try { const j = JSON.parse(localStorage.getItem(SPEAKER_KEY) ?? 'null'); if (j) { v = Math.max(0, Math.min(1, Number(j.v) || 0)); off = !!j.off; } } catch {}
  const save = () => { try { localStorage.setItem(SPEAKER_KEY, JSON.stringify({ v, off })); } catch {} };
  return {
    volume: () => v, off: () => off, gain: () => (off ? 0 : v),
    setVolume(x) { v = Math.max(0, Math.min(1, x)); if (v > 0) off = false; save(); if (actx) volumes(); },
    setOff(on) { off = !!on; save(); if (actx) volumes(); },
  };
})();
addSettingsSection({ id: 'speaker', title: () => t('app.speaker.title'), icon: 'speaker', order: 12, render(box) {
  const row = document.createElement('div'); row.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:13px';
  const r = document.createElement('input'); r.type = 'range'; r.min = '0'; r.max = '100'; r.value = String(Math.round(speaker.volume() * 100)); r.style.cssText = 'flex:1;accent-color:#1b1410'; r.setAttribute('aria-label', t('app.speaker.volume'));
  const n = document.createElement('span'); n.style.cssText = 'width:38px;text-align:right'; n.textContent = r.value + '%';
  const b = document.createElement('button'); b.type = 'button'; b.style.cssText = 'font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:2px 10px;background:#fff;cursor:pointer;white-space:nowrap';
  const draw = () => { label(b, speaker.off() ? 'speaker_off' : 'speaker', t(speaker.off() ? 'app.speaker.isOff' : 'app.speaker.turnOff')); b.style.background = speaker.off() ? '#FDD03B' : '#fff'; };
  r.oninput = () => { speaker.setVolume(r.value / 100); n.textContent = r.value + '%'; draw(); };
  b.onclick = () => { speaker.setOff(!speaker.off()); draw(); };
  draw(); row.append(icon('speaker'), r, n, b);
  const hint = document.createElement('p'); hint.textContent = t('app.speaker.hint'); hint.style.cssText = 'font-size:12px;color:#666';
  box.append(row, hint);
} });
function volumes() {
  if (actx?.state === 'suspended') actx.resume();
  if (audioProvider.isAgora()) audioProvider.sync();
  if (!actx || actx.state === 'closed') return;
  const k = speaker.gain();
  for (const p of audible()) { const id = p.id;
    if (p.clip) p.clip.gain.gain.setTargetAtTime(loudness(id, p) * k, actx.currentTime, 0.15);
    // ducked (I just pressed 按住说话 to interrupt its 秘书, public/secretary.js): down at once, not eased
    if (p.gainNode) { if (p.duckUntil > performance.now()) p.gainNode.gain.setTargetAtTime(loudness(id, p) * k * 0.1, actx.currentTime, 0.01); else p.gainNode.gain.setTargetAtTime(loudness(id, p) * k, actx.currentTime, 0.15); }
  }
}
/** An agent's line spoken aloud (deck say --voice): the office sends it only to people who hear that agent. Fetched
 *  through /voice/clip (same origin, so the bytes can be read) and played through the voice graph: the volume their
 *  voice would have (a gain node, which iPhone Safari honours where it ignores <audio>.volume), nothing while voice is
 *  off, one line after another, and the otter shows as speaking meanwhile (its level). */
function playClip(m) {
  const p = peers.get(m.id); if (!p || typeof m.url !== 'string') return;
  const requestedOn = local.gen;
  p.clips = (p.clips || Promise.resolve()).then(async () => {
    const ctx = actx, g = requestedOn;
    if (!ctx || stale(g) || ctx.state === 'closed' || voiceStopped) return;
    const r = await fetch(vq('/voice/clip?u=' + encodeURIComponent(m.url))); if (!r.ok) return;
    if (ctx !== actx || stale(g) || ctx.state === 'closed') return;
    const buf = await ctx.decodeAudioData(await r.arrayBuffer());
    if (ctx !== actx || stale(g) || ctx.state === 'closed' || voiceStopped || !peers.has(m.id)) return;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const gain = ctx.createGain(); gain.gain.value = loudness(m.id, p) * speaker.gain();
    src.connect(gain).connect(ctx.destination);
    const clip = (p.clip = { gain, level: analyser(src) }); // measured before my 扬声器 volume: muted here, still talking
    await new Promise((done) => { src.onended = done; src.start(); });
    gain.disconnect(); if (p.clip === clip) p.clip = null;
  }).catch((e) => { if (!stale(requestedOn)) { console.error('voice clip:', e); p.clip = null; } });
}

// ---- the gather flag: going there is a trip like any click (walk, or sail and walk)
function goRally() {
  if (!rally) return;
  void mover.travelTo(rally.x, rally.y);
}
// 📍 回到我 (or Space): shown while the camera doesn't follow me (deck/camera.js: dragged away; clicking about keeps it there)
if (!$('recenter')) { const b = document.createElement('button'); b.id = 'recenter'; b.style.display = 'none'; b.style.cssText = 'display:none;position:fixed;right:68px;bottom:16px;font:inherit;border:3px solid #1b1410;border-radius:999px;padding:6px 14px;background:#FDD03B;cursor:pointer'; document.body.appendChild(b); }
// its pin and words, in the reader's language (again on every change)
{ const draw = () => put($('recenter'), 'pin', t('app.recenter.label')); draw(); onLang(draw); }
$('recenter').onclick = () => camera.follow();
// Space recenters too, except in 按住说话 mode (there Space is the talk key: voice-settings.js)
$('recenter').onpointerenter = () => { $('recenter').title = t(voiceMode() === 'ptt' ? 'app.recenter.tip' : 'app.recenter.tipSpace'); };

// ---- who is talking, what they look like (the map draws them: deck/entities.js)
function speaking(p) {
  if (audioProvider.isAgora()) return (p === me ? audioProvider.localLevel() : audioProvider.remoteLevel(p.id)) > 0.02 || !!(p.clip && p.clip.level() > 0.02);
  const tr = local.mic?.getAudioTracks()[0];
  if (p === me) return !!(tr?.enabled && local.level && local.level() > 0.02);
  return !!(p.level && p.level() > 0.02) || !!(p.clip && p.clip.level() > 0.02);
}
// a saved look (from before bodies: the original otter), else one made from their Raft account, a new body included
function lookOf(p) { return lookFor(p.look, p.seed || p.id); }

// ---- text chat: a bubble over the sender for whoever can hear them (the server decides who), and a log of what I got
const BUBBLE_MS = 6000, CHAT_KEEP = 100;
if (!$('chatbox')) {
  const box = document.createElement('div'); box.id = 'chatbox'; box.style.display = 'none';
  box.innerHTML = '<input id="chatin" maxlength="1600" autocomplete="off"><span id="chatcount"></span>';
  tr(box.querySelector('#chatin'), null, undefined, { placeholder: 'app.chat.placeholder' });
  const log = document.createElement('div'); log.id = 'chatlog'; log.className = 'closed';
  log.innerHTML = '<div id="chathead"><span id="chattitle"></span><span id="chatunread"></span><span id="chatfold"></span></div><div id="chatlist"><div class="empty"></div></div>';
  tr(log.querySelector('#chattitle'), 'app.chat.title'); tr(log.querySelector('.empty'), 'app.chat.empty');
  document.body.append(box, log);
  $('chathead').prepend(icon('chat')); $('chatfold').append(icon('caret_up'));
  const st = document.createElement('style');
  st.textContent = `#chatbox{position:fixed;left:50%;bottom:calc(var(--barh, calc(68px + var(--sb, 0px))) + 8px);transform:translateX(-50%);width:min(520px,calc(100vw - 32px));display:flex;align-items:center;gap:8px;background:#fff8e7;border:3px solid #1b1410;border-radius:14px;padding:6px 10px;box-sizing:border-box}
  #chatin{flex:1;font:inherit;padding:6px 8px;border:2px solid #1b1410;border-radius:10px;min-width:0} #chatcount{font-size:12px;color:#888;width:52px;text-align:right}
  #chatlog{position:fixed;right:16px;bottom:calc(var(--barh, calc(68px + var(--sb, 0px))) + 44px);width:300px;max-width:calc(100vw - 32px);background:#fff8e7;border:3px solid #1b1410;border-radius:14px;box-sizing:border-box;overflow:hidden}
  #chathead{padding:4px 10px;font-weight:600;cursor:pointer;user-select:none;display:flex;gap:6px;align-items:center} #chatfold{margin-left:auto}
  #chatunread{background:#ef4444;color:#fff;border-radius:999px;padding:0 7px;font-size:12px;display:none}
  #chatlist{max-height:220px;overflow:auto;font-size:13px;background:#fff;border-top:2px solid #1b1410;padding:4px 8px}
  #chatlog.closed #chatlist{display:none} #chatlist .line{margin:2px 0;word-break:break-word} #chatlist .t{color:#999;font-size:11px;margin-right:4px}
  #chatlist .n{font-weight:700;margin-right:4px} #chatlist .mine .n{color:#7c3aed} #chatlist .empty{color:#888}
  #chatlist .voice .px-icon{margin-right:3px;opacity:.7} #chatlist .voice > span:last-child{color:#444}`;
  document.head.appendChild(st);
}
let chatUnread = 0;
function chatFold(open) {
  $('chatlog').classList.toggle('closed', !open); $('chatfold').replaceChildren(icon(open ? 'caret_down' : 'caret_up'));
  if (open) { chatUnread = 0; $('chatunread').style.display = 'none'; $('chatlist').scrollTop = 1e9; }
}
$('chathead').onclick = () => chatFold($('chatlog').classList.contains('closed'));
function chatOpen() {
  if (!me) return;
  if (meeting.full()) return meeting.openChat(); // the meeting covers the map: its own chat panel
  mover.keys.clear(); mover.unpush(); // whatever was held stops; typing must not walk
  $('chatbox').style.display = 'flex'; $('chatin').focus(); chatCount();
}
function chatClose() { $('chatin').value = ''; $('chatbox').style.display = 'none'; $('chatin').blur(); }
// 200 characters, counted like the server does (code points), never cutting an emoji or accent in half
const CHAT_LEN = 200, graphemes = window.Intl?.Segmenter ? new Intl.Segmenter('zh', { granularity: 'grapheme' }) : null;
function chatCut(s) {
  if ([...s].length <= CHAT_LEN) return s;
  if (!graphemes) return [...s].slice(0, CHAT_LEN).join('');
  let out = '', n = 0;
  for (const { segment } of graphemes.segment(s)) { const k = [...segment].length; if (n + k > CHAT_LEN) break; out += segment; n += k; }
  return out;
}
const chatCount = () => {
  const el = $('chatin'), cut = chatCut(el.value);
  if (cut !== el.value) el.value = cut;
  const n = [...el.value].length; $('chatcount').textContent = n > 150 ? `${n}/${CHAT_LEN}` : '';
};
$('chatin').addEventListener('input', chatCount);
$('chatin').addEventListener('keydown', (e) => {
  e.stopPropagation(); // nothing typed here moves the otter or fires a shortcut
  if (e.isComposing || e.keyCode === 229) return; // Enter that confirms a Chinese IME word is not "send"
  if (e.key === 'Escape') { e.preventDefault(); chatClose(); }
  else if (e.key === 'Enter') { e.preventDefault(); const text = $('chatin').value.trim(); if (text) send({ t: 'chat', text }); chatClose(); }
});
$('chatin').addEventListener('keyup', (e) => e.stopPropagation());
$('chatin').addEventListener('blur', () => { if (!$('chatin').value.trim()) $('chatbox').style.display = 'none'; });
$('chat').onmousedown = (e) => e.preventDefault(); // keep the input's focus, so the button can close it
$('chat').onclick = () => ($('chatbox').style.display === 'none' ? chatOpen() : chatClose());
/** A chat line: a bubble over the sender, the meeting's chat, and the log. `voice`: a transcript line
 *  (captionGot): a mic icon before the name, no meeting chat (the room has its 字幕), not counted as unread (it's what
 *  I just heard), and a bubble only if asked for (`bubble`: 显示字幕 on the map). */
function chatGot(m, { voice = false, bubble = !voice } = {}) {
  const p = m.id === me?.id ? me : peers.get(m.id);
  // an agent's 秘书 (office/secretary.ts, sec: true) speaks over the agent's otter, marked as the secretary's words
  if (p && bubble) p.say = { text: m.sec ? t('app.chat.secretary', { text: m.text }) : m.text, at: Date.now(), until: Date.now() + BUBBLE_MS };
  secretary.heard(m);
  if (!voice) meeting.chat(m, m.id === me?.id);
  else if (!stt.log()) return;
  const list = $('chatlist'); list.querySelector('.empty')?.remove();
  const line = document.createElement('div'); line.className = 'line' + (m.id === me?.id ? ' mine' : '') + (voice ? ' voice' : '');
  if (voice) line.append(tr(icon('mic', { title: t('app.chat.transcript') }), null, undefined, { title: 'app.chat.transcript', 'aria-label': 'app.chat.transcript' }));
  const tm = document.createElement('span'); tm.className = 't'; tm.textContent = fmtTime(m.at || Date.now());
  const n = document.createElement('span'); n.className = 'n'; put(n, (p || onlineLast?.list.find((o) => o.id === m.id))?.kind === 'agent' ? 'robot' : null, t('app.chat.from', { name: m.name || t('app.chat.anon') }));
  const x = document.createElement('span'); put(x, m.icon, m.text);
  line.append(tm, n, x);
  const stick = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
  list.appendChild(line);
  while (list.children.length > CHAT_KEEP) list.firstChild.remove();
  if (stick) list.scrollTop = 1e9;
  if ($('chatlog').classList.contains('closed') && m.id !== me?.id && !voice) { chatUnread++; $('chatunread').textContent = chatUnread; $('chatunread').style.display = ''; }
}

/** {t:"caption"}: a final line goes to the chat log (and over the speaker's head with 显示字幕, off a meeting room);
 *  in a 会议室 the strip shows it, and the words so far (partial: only there, nowhere else) as they come. */
function captionGot(m) {
  const mine = m.id === me?.id;
  if (meeting.room()) meeting.caption(m, mine);
  if (!m.partial) chatGot(m, { voice: true, bubble: !meeting.room() && stt.ccMap() });
}

// ---- who's online: the server's count (one per person) and where everyone is
if (!$('online')) {
  const b = document.createElement('button'); b.id = 'online'; put(b, 'otter', t('app.online.count', { n: '–' }));
  const l = document.createElement('div'); l.id = 'onlineList'; l.style.display = 'none';
  if ($('topleft')) $('topleft').append(b, l); else { b.style.cssText = 'position:fixed;top:44px;left:12px'; document.body.append(b, l); }
}
let onlineOpen = false, onlineLast = null;
onLang(() => { if (onlineLast) showOnline(onlineLast); else put($('online'), 'otter', t('app.online.count', { n: '–' })); });
function showOnline(m) {
  onlineLast = m;
  put($('online'), 'otter', t('app.online.count', { n: m.n }));
  const box = $('onlineList'); box.replaceChildren();
  for (const o of [...m.list].sort((a, b) => (a.kind === 'agent') - (b.kind === 'agent'))) { // humans first, then agents
    const row = document.createElement('div'); row.className = 'row' + (o.id === me?.id ? ' mine' : '');
    const n = document.createElement('span'); n.className = 'n'; put(n, o.kind === 'agent' ? 'robot' : null, o.guest ? o.name.replace(/（访客）$/, '') : o.visitor ? o.name.replace(/（来自[^）]*）$/, '') : o.name);
    const op = o.id === me?.id ? me : peers.get(o.id);
    if (transcribed(op)) n.append(' ', icon('transcribing', { title: view.tagTip(op, 'transcribing') })); // as on their name tag
    let stw = null; // their status in words (离开 · 午休), after the name
    if (o.kind !== 'agent') { // their status (public/status.js): the icon before the name, the note in the tooltip
      const s = statusOf(o.id === me?.id ? me : peers.get(o.id));
      n.prepend(icon(STATES[s?.s ?? 'available'].icon, { title: statusLine(s) }), ' ');
      row.title = t('app.online.rowTip', { name: o.name, status: statusLine(s) });
      if (s) { stw = document.createElement('span'); stw.className = 'st'; stw.textContent = s.note ? `${STATES[s.s].name} · ${s.note}` : STATES[s.s].name; stw.style.cssText = `font-size:12px;color:${STATES[s.s].color};white-space:nowrap;font-weight:600;overflow:hidden;text-overflow:ellipsis;min-width:0`; }
    }
    const w = document.createElement('span'); w.className = 'w'; w.textContent = o.where;
    row.append(...[n, stw, w].filter(Boolean)); // (the words were dropped before: added next to the name before it was in the row)
    if (o.guest) { // 访客: a tag after the name, who invited them in the tooltip
      const g = document.createElement('span'); g.className = 'gtag'; g.textContent = t('app.online.guest'); g.title = t('app.online.guestBy', { by: o.guest.by || t('app.online.member') });
      n.after(g); row.title = `${o.name} · ${g.title}`;
    }
    if (o.visitor) { // 串门: where they come from, after the name
      const g = document.createElement('span'); g.className = 'gtag vtag'; const from = o.visitor.server_name || t('app.visit.otherServer'); g.textContent = t('app.online.from', { server: from }); g.title = t('app.online.visiting', { server: from });
      n.after(g); row.title = `${o.name} · ${g.title}`;
    }
    if (o.kind !== 'agent' && Number.isInteger(o.rtt)) { // people report their own latency; agents don't, and show nothing
      const r = document.createElement('span'); r.className = 'rtt'; r.title = t('app.online.rtt', { name: o.name });
      r.append(dot(rttLevel(o.rtt)), `${o.rtt} ms`); row.append(r);
    }
    const act = agentDoor.onlineRow(row, o); // an agent's menu: 「请出去」 (public/agent-door.js)
    box.append(...[row, act].filter(Boolean));
  }
  if (guestsUi) { // members: 邀请访客 from here too (public/guests.js)
    const b = document.createElement('button'); b.className = 'ginv'; b.type = 'button'; put(b, 'people', t('app.online.inviteGuests'));
    b.onclick = (e) => { e.stopPropagation(); guestsUi.open(); }; box.appendChild(b);
    // and 去串门 (public/visits.js: 设置 → 串门)
    const v = document.createElement('button'); v.className = 'ginv'; v.type = 'button'; put(v, 'pin', t('app.visit.go'));
    v.onclick = (e) => { e.stopPropagation(); openVisits(e.detail === 0); }; box.appendChild(v);
  }
}
$('online').onclick = () => { onlineOpen = !onlineOpen; $('onlineList').style.display = onlineOpen ? '' : 'none'; };

// ---- latency: a "ping" every PING_MS while connected (answered by the office's runtime), shown in the corner readout
// (signal bars and the round trip); its details (hover, or a tap: the small panel under it) add voice (the SFU
// connection's round trip and packet loss). Each client reports its own median to the office (at most every
// RTT_REPORT_MS), which shows it in the online list.
// The details also show jitter and stalls, the same measures as OtterBonk's HUD (Imprint/Gogo, #wg-deck): a ping a
// second; jitter is the mean deviation of the round trips kept; the office sends nothing steady to time gaps on, so a
// stall here is a round trip STALL_MS slower than usual, or one not back within PING_MS + STALL_MS.
const PING_MS = 1000, PING_KEEP = 10, RTT_REPORT_MS = 10_000;
/** No pong for this long (less time this tab was busy): slow (「999+ ms」). Stalls: STALL_MS late; counted over STALL_WINDOW_MS. */
const SLOW_AFTER_MS = 5000, STALL_MS = 250, STALL_WINDOW_MS = 60_000;
/** Round trips under RTT_GOOD_MS are good, under RTT_SLOW_MS ok, else (or no pong within PING_MS) bad. */
const RTT_GOOD_MS = 120, RTT_SLOW_MS = 300;
const RTT_COLORS = { good: '#22c55e', ok: '#f59e0b', bad: '#ef4444' }; // bad: the same red as 「广播中」 and unread chat
const rttLevel = (ms) => (ms == null ? 'bad' : ms < RTT_GOOD_MS ? 'good' : ms < RTT_SLOW_MS ? 'ok' : 'bad');
// The connection readout (artin 11-02: not a pill in the top bar, a small thing in a corner; 999 ms when the line is
// down): a chip in the top right corner, above the top bar's pills, with signal bars and the round trip. State as
// data: state -> { bars lit (of 4), look, text key }; the reason a line is down (net.why.*) shows in its details.
const NET = {
  wait: { bars: 0, look: 'wait', text: 'net.wait' }, // connecting (net.why.connecting), or connected and not measured yet (measuring): 「– ms」
  good: { bars: 4, look: 'good', text: 'net.ms' },
  ok: { bars: 3, look: 'ok', text: 'net.ms' },
  bad: { bars: 1, look: 'bad', text: 'net.ms' },
  slow: { bars: 1, look: 'bad', text: 'net.slow' }, // no pong for SLOW_AFTER_MS: 「999+ ms」
  retry: { bars: 0, look: 'down', text: 'net.retry' }, // dropped, the next try in 3 s: 「999 ms · 重连中」, its dot blinks
  off: { bars: 0, look: 'down', text: 'net.off' }, // stopped for good (signed out, another window, ...): 「离线」
};
{
  const st = document.createElement('style');
  st.textContent = `.rttdot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px;vertical-align:0;border:1px solid #1b1410}
  ${Object.entries(RTT_COLORS).map(([k, c]) => `.rttdot.${k}{background:${c}}`).join(' ')}
  #onlineList .rtt{color:#666;white-space:nowrap;font-size:12px;font-variant-numeric:tabular-nums}
  #net{position:fixed;top:var(--st);right:var(--sr);z-index:14;display:flex;align-items:center;gap:4px;box-sizing:border-box;height:12px;margin:0;padding:0 6px 0 5px;border:0;border-radius:0 0 0 6px;background:rgba(27,20,16,.55);color:#fff8e7;font:600 10px/12px var(--ui-font,-apple-system,system-ui,sans-serif);font-variant-numeric:tabular-nums;white-space:nowrap;cursor:pointer;opacity:.8;-webkit-tap-highlight-color:transparent}
  #net:hover,#net.open{opacity:1}
  #net .bars{display:inline-flex;align-items:flex-end;gap:1px;height:8px}
  #net .bars i{display:block;width:2px;background:rgba(255,248,231,.28)}
  #net .bars i:nth-child(1){height:2px} #net .bars i:nth-child(2){height:4px} #net .bars i:nth-child(3){height:6px} #net .bars i:nth-child(4){height:8px}
  ${Object.entries(RTT_COLORS).map(([k, c]) => `#net.${k} .bars i.on{background:${c}}`).join(' ')}
  #net .dot{display:none;width:6px;height:6px;border-radius:50%;background:#fff8e7}
  #net.down{background:#dc2626;color:#fff;opacity:1} #net.down .bars{display:none} #net.down .dot{display:block}
  #net.retry .dot{animation:netblink 1s steps(2,start) infinite}
  @keyframes netblink{to{visibility:hidden}}
  @media (pointer:coarse){#net{height:14px;font-size:11px;line-height:14px;padding:0 8px 0 7px}}
  #netinfo{position:fixed;top:calc(var(--st) + 20px);right:calc(8px + var(--sr));z-index:16;box-sizing:border-box;max-width:calc(100vw - 16px);background:#fff8e7;border:2px solid #1b1410;border-radius:10px;padding:6px 10px;font:12px/1.6 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;white-space:pre-line;font-variant-numeric:tabular-nums}`;
  document.head.appendChild(st);
}
const dot = (level) => { const d = document.createElement('span'); d.className = 'rttdot ' + level; return d; };
const netUi = (() => {
  const b = document.createElement('button'); b.id = 'net'; b.type = 'button';
  const bars = document.createElement('span'); bars.className = 'bars';
  for (let i = 0; i < 4; i++) bars.appendChild(document.createElement('i'));
  const blink = document.createElement('span'); blink.className = 'dot';
  const words = document.createElement('span');
  b.append(bars, blink, words);
  const info = document.createElement('div'); info.id = 'netinfo'; info.style.display = 'none';
  document.body.append(b, info);
  let state = 'wait', ms = null, why = 'connecting', lines = () => [];
  const open = () => info.style.display !== 'none';
  function draw() {
    const s = NET[state];
    b.className = s.look + (state === 'retry' ? ' retry' : '') + (open() ? ' open' : '');
    [...bars.children].forEach((i, k) => i.classList.toggle('on', k < s.bars));
    words.textContent = t(s.text, { ms: ms ?? '–' });
    const tip = s.look === 'down' || state === 'wait' ? [t(`net.why.${why}`)] : lines();
    b.title = tip.join('\n'); b.setAttribute('aria-label', [t('net.label'), words.textContent, ...tip].join(' · '));
    if (open()) info.textContent = tip.join('\n');
  }
  b.onclick = (e) => { e.stopPropagation(); info.style.display = open() ? 'none' : ''; draw(); };
  document.addEventListener('pointerdown', (e) => { if (open() && !b.contains(e.target) && !info.contains(e.target)) { info.style.display = 'none'; draw(); } }, true);
  onLang(draw); draw();
  return {
    /** A state of NET; ms: the round trip to show; reason: why the line is down (net.why.<reason>); details: () => lines. */
    set(st, o = {}) { state = st; ms = o.ms ?? null; if (o.reason) why = o.reason; if (o.details) lines = o.details; draw(); },
    why: () => t(`net.why.${why}`),
  };
})();
/** The readout, the line down or stopped: `state` retry | off, `reason` a net.why.* key. Called before netUi exists
 *  (another tab signs out while this one starts up): kept, and shown once it does. */
var netEarly; // a var, not initialised here: set before this line runs (no TDZ) and still there after it
function setNet(state, reason) { try { netUi.set(state, { reason }); } catch { netEarly = [state, reason]; } }
if (netEarly) setNet(...netEarly);
/** Why the line is down, in words (the loading screen says it too). */
const netWhy = () => netUi.why();
/** How long this tab itself was busy (long tasks, where the browser reports them: Chromium) between two
 *  performance.now() times. A pong that waited behind the page starting up says little about the network. */
const pageBusyMs = (() => {
  const busy = []; let obs = null;
  const keep = (list) => { for (const e of list) busy.push([e.startTime, e.startTime + e.duration]); busy.splice(0, busy.length - 50); };
  try { obs = new PerformanceObserver((l) => keep(l.getEntries())); obs.observe({ type: 'longtask' }); } catch { obs = null; }
  return (a, b) => { if (obs) keep(obs.takeRecords()); let ms = 0; for (const [x, y] of busy) ms += Math.max(0, Math.min(y, b) - Math.max(x, a)); return ms; };
})();
const latency = (() => {
  let timer = null, sentAt = 0, heardAt = 0, waiting = false, slow = false, samples = [], reportedAt = 0, welcomed = false;
  let stalls = [], stallOpen = false; // when each stall happened; the ping in flight already counted as one
  let voice = null, lastIn = null; // the voice line's last stats, and the inbound counters they were taken from
  let where = null; // the welcome's "net": { edge, server, serverLoc } colos
  const median = () => { if (!samples.length) return null; const s = [...samples].sort((a, b) => a - b); return s[s.length >> 1]; };
  /** Mean absolute deviation of the round trips kept (ms), once there are a few. */
  const jitter = () => { if (samples.length < 3) return null; const m = samples.reduce((a, b) => a + b, 0) / samples.length; return Math.round(samples.reduce((a, b) => a + Math.abs(b - m), 0) / samples.length); };
  const stall = (now) => { stalls.push(now); stalls = stalls.filter((t) => now - t < STALL_WINDOW_MS); };
  function show() {
    if (!timer) return; // disconnected meanwhile: the readout says so already
    const ms = median(), state = slow ? 'slow' : ms == null ? 'wait' : rttLevel(ms);
    netUi.set(state, { ms, reason: 'measuring', details });
  }
  /** The readout's details: the round trip, jitter and stalls, voice, and where the office runs. */
  function details() {
    const ms = median(), tip = [slow ? t('net.tip.rttSlow', { ms: SLOW_AFTER_MS }) : t('net.tip.rtt', { ms: ms ?? '–' })];
    const j = jitter(), n = stalls.filter((x) => performance.now() - x < STALL_WINDOW_MS).length;
    tip.push(t('net.tip.jitter', { jitter: j ?? '–', n }));
    if (voice?.rtt != null) tip.push(voice.loss != null ? t('net.tip.voiceLoss', { ms: voice.rtt, loss: voice.loss }) : t('net.tip.voice', { ms: voice.rtt }));
    // where the office is: the edge this tab came in through → the colo the office runs in (either may be unknown locally)
    if (where?.edge || where?.server) {
      const at = { edge: where.edge ?? '?', server: where.server ?? '?' };
      tip.push(where.serverLoc ? t('net.tip.whereLoc', { ...at, loc: where.serverLoc }) : t('net.tip.where', at));
    }
    return tip;
  }
  /** My median to the office (for the online list), at most every RTT_REPORT_MS. */
  function report() {
    const ms = median(), now = Date.now();
    if (welcomed && ms != null && now - reportedAt >= RTT_REPORT_MS) { reportedAt = now; send({ t: 'rtt', ms: Math.min(ms, 10_000) }); }
  }
  function tick() {
    const now = performance.now(), waited = now - sentAt;
    // The one in flight is late: a stall (once per ping), and after SLOW_AFTER_MS (less this tab's own busy time, or
    // twice that regardless) slow (「999+ ms」). A new ping goes out only when the last came back or is given up on.
    if (waiting && !stallOpen && waited - pageBusyMs(sentAt, now) >= PING_MS + STALL_MS) { stallOpen = true; stall(now); }
    if (waiting && ((waited >= SLOW_AFTER_MS - 250 && waited - pageBusyMs(sentAt, now) >= 500) || now - heardAt >= 2 * SLOW_AFTER_MS - 250)) slow = true;
    if ((!waiting || waited >= SLOW_AFTER_MS) && net.ping()) { sentAt = performance.now(); waiting = true; stallOpen = false; }
    voiceStats().finally(show);
  }
  /** The SFU connection's round trip (selected candidate pair) and audio loss since the last look; never throws. */
  async function voiceStats() {
    const pc = local.pc;
    if (!pc || pc.connectionState === 'closed') { voice = null; lastIn = null; return; }
    try {
      const stats = await pc.getStats();
      let pairId = null, pair = null, lost = 0, got = 0;
      stats.forEach((r) => { if (r.type === 'transport' && r.selectedCandidatePairId) pairId = r.selectedCandidatePairId; });
      stats.forEach((r) => {
        if (r.type === 'candidate-pair' && (pairId ? r.id === pairId : r.selected || (r.nominated && r.state === 'succeeded'))) pair = r;
        if (r.type === 'inbound-rtp' && r.kind === 'audio') { lost += r.packetsLost || 0; got += r.packetsReceived || 0; }
      });
      const rtt = pair?.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null;
      let loss = null;
      if (lastIn) { const dl = lost - lastIn.lost, dg = got - lastIn.got; if (dl >= 0 && dg >= 0 && dl + dg > 0) loss = Math.round((dl / (dl + dg)) * 1000) / 10; }
      lastIn = { lost, got }; voice = { rtt, loss };
    } catch { voice = null; }
  }
  return {
    start() { this.stop(); heardAt = performance.now(); tick(); timer = setInterval(tick, PING_MS); },
    stop() { clearInterval(timer); timer = null; waiting = false; slow = false; samples = []; reportedAt = 0; welcomed = false; stalls = []; stallOpen = false; },
    /** The office greeted this connection: it takes reports now. */
    welcome(net) { welcomed = true; where = net ?? null; report(); },
    pong() {
      if (!waiting) return;
      waiting = false; slow = false;
      // Less the time this tab itself was busy (say, the map starting up): the pong may have waited behind that.
      const now = performance.now(); heardAt = now;
      const ms = Math.max(0, Math.round(now - sentAt - pageBusyMs(sentAt, now))), usual = median();
      if (!stallOpen && usual != null && ms > usual + STALL_MS) stall(now); // much slower than this connection's usual
      stallOpen = false;
      samples.push(ms); if (samples.length > PING_KEEP) samples.shift();
      report(); show();
    },
  };
})();

// ---- the game huts (public/games.js)
const games = initGames({ send, me: () => me, zoneAt, peers: () => peers, stopWalking: () => { mover.keys.clear(); mover.unpush(); mover.clear(); },
  // walk (and sail) to a hut: an OtterBonk invite brings me to its room
  walkToZone: (zid) => { const z = MAP.zones.find((q) => q.id === zid); return z ? mover.travelTo(Math.floor(z.x + z.w / 2), Math.floor(z.y + z.h / 2)) : Promise.resolve(false); },
  guest: () => !!(guestMe || visitMe), micOn: () => !!local.mic && !muted }); // (micOn: the hut games' voice note)
hutSealed = games.sealed;
// ---- 五子棋 at the tables (public/gomoku.js)
const gomoku = initGomoku({ send, me: () => me, MAP, mover, toast });
// ---- the 牌桌: 德州扑克 … (public/cards.js)
const cards = initCards({ send, me: () => me, MAP, mover, toast });
// ---- 邀请成员 (public/members.js): the one picker of members online, and the invite cards I get
const members = initMembers({ me: () => me, peers: () => peers, zoneAt, statusOf, STATES, agentName, gomoku, mover, MAP, toast });
// ---- inviting members to 五子棋 / 你画我猜 (public/invite.js)
const invite = initInvite({ send, me: () => me, peers: () => peers, zoneAt, gomoku, toast, members, MAP, guest: () => !!(guestMe || visitMe) }); // a guest (or a visitor) accepts invites, sends none
// ---- 钓鱼 (public/fishing.js): everyone's rods on the map; the mini-game itself (public/fishing-mode.js) loads when
//      someone opens it at the shore (pick.js: click the sea → 钓鱼), and shows nothing on the page until then
const fishing = initFishing({ send, me: () => me, peers: () => peers, MAP, toast });
// ---- a click selects (a person's card, an object's actions), a double click walks (public/pick.js, deck/input.js)
const calHook = {}; // 日历 (public/calendar.js, made further down): the room card's bookings, the room-entered hook
const pick = initPick({ MAP, me: () => me, peers: () => peers, mover, sit, gomoku, fishing, greet, rally: rallyNow, goRally, zoneAt, toast, roomSchedule: (z) => calHook.c?.roomSchedule(z) ?? null,
  blocked: () => meeting.full() || mapEditor.active(), desks });
// a 牌桌 (public/cards.js): 坐下打牌
pick.add((wx, wy) => cards.pickTarget(wx, wy));
// a deck 白板 (public/whiteboard.js): 打开白板 (walks up to it first when it's out of reach)
pick.add((wx, wy) => {
  const b = whiteboard.boardAt(wx, wy); if (!b) return null;
  return { key: 'board:' + b.id, mark: () => ({ x: b.x, y: b.y + 0.1, r: 1.2 }),
    view: () => ({ icon: 'board', title: b.name || t('app.board.name'), sub: t('app.board.sub'), actions: [{ icon: 'board', text: t('app.board.open'), go: () => whiteboard.mapClick(b.x, b.y - 1) }] }),
    tip: () => t('app.board.tip') };
});
// the 活动舱's 排行榜 board (the map's kiosks, deck/kiosks-view.js): every game's 战绩 / 排行榜 (public/records.js), with
// its game picker; a guest or a visitor sees the board but has no records here (office/records.ts refuses them)
const kioskAt = (wx, wy) => (MAP.kiosks || []).find((k) => wx >= k.x - 1.1 && wx <= k.x + 1.1 && wy >= k.y - 2.6 && wy <= k.y + 0.2) ?? null;
pick.add((wx, wy) => {
  const k = kioskAt(wx, wy); if (!k || k.kind !== 'leaderboard') return null;
  const off = !!(guestMe || visitMe);
  return { key: 'kiosk:' + k.id, mark: () => ({ x: k.x, y: k.y + 0.1, r: 1.1 }),
    view: () => ({ icon: 'trophy', title: t('kiosk.leaderboard.name'), sub: t(off ? 'kiosk.leaderboard.off' : 'kiosk.leaderboard.sub'),
      actions: off ? [] : [{ icon: 'trophy', text: t('kiosk.leaderboard.open'), go: () => openRecords() }] }),
    tip: () => t('kiosk.leaderboard.tip') };
});

// ---- 通话 (public/call.js): a group call wherever the otters are; its view is the meeting view below
callUi = initCall({ send, toast, me: () => me, peers: () => peers, members, floorName, refreshFloors, guest: !!(guestMe || visitMe), dropMedia, link: { top: (k) => meetLink.top(k) }, beforeHangup: () => stopShare(),
  zoneAt, walkToZone: (id, name) => members.walkToZone(id, name),
  changed: () => { enforceShareScope(); syncAudio(); meeting.refresh(); meeting.recam(); } });
// 通话 in the bar: start one (the picker); in one: its view; still ringing alone: 取消 (hang up). In a 会议室: off, with
// the hint on it (a click says it too: callUi.pick)
$('call').onclick = () => { const c = callUi.active(); if (!c) callUi.pick(); else if (c.members.length > 1) meeting.openFull(); else callUi.hangup(); };
setInterval(() => { const c = callUi.active(), st = c && c.members.length < 2 ? 'ringing' : c ? 'live' : 'idle', off = callUi.roomBlocked(), b = $('call');
  if (b.dataset.t !== st) { b.dataset.t = st; setBar('call', st); b.classList.toggle('live', !!c); }
  b.classList.toggle('off', off); b.setAttribute('aria-disabled', String(off)); b.title = off ? t('call.err.E_IN_ROOM') : ''; }, 500);
if (guestMe || visitMe) $('call').style.display = 'none'; // a guest (or a visitor from another server) answers calls, starts none (office.ts GUEST_DENIED)
// ---- 会议纪要 (public/notes.js): recording a 会议室, the notes written from it
const notes = initNotes({ send, toast, room: () => meeting.room(), guest: !!(guestMe || visitMe) });
// ---- 会议室 (public/meeting.js): the Zoom-like view in a meeting room, and my camera there
// ---- 主持人 (public/meethost.js): each 会议室's host, its lock, the 认领主持人 vote; what the host did to me
meetHost = initMeetHost({ send, toast, me: () => me, members, zoneAt, invitedTo: (id) => meetinv.invitedTo(id), guest: !!(guestMe || visitMe), leave: () => meeting.leave(),
  mic: { muted: () => muted, set: (on) => setMute(on) } });
const meeting = initMeeting({ me: () => me, peers: () => peers, zoneAt, mover, speaking, lookOf, buildSheet, otterImgs, agentName, send, toast,
  raftChannel: (slot, room) => raftLinks.fill(slot, room),
  mic: { muted: () => muted, ready: () => audioProvider.isAgora() ? audioProvider.microphoneReady() : !!local.mic?.getAudioTracks()[0], toggle: () => setMute(!muted) },
  share: { live: () => !!share.mid, stream: () => share.stream, toggle: () => $('share').click(), stop: () => stopShare() },
  rtc: { ...rtc, ready: () => !audioProvider.isAgora() && rtc.ready(), videoAvailable: () => !audioProvider.isAgora() },
  cc: { available: () => !audioProvider.isAgora() || audioProvider.sttAvailable(), on: () => (!audioProvider.isAgora() || audioProvider.sttAvailable()) && stt.ccMeet(), toggle: () => { if (!audioProvider.isAgora() || audioProvider.sttAvailable()) stt.setCcMeet(!stt.ccMeet()); }, me: () => stt.on(), tip: () => stt.tip(), tipOther: STT_TIP_OTHER }, invite: guestMe || visitMe ? undefined : { open: () => meetinv.open() }, notes, host: meetHost, // guests can't invite (office.ts GUEST_DENIED)
  call: { view: () => callUi.view(), hangup: () => callUi.hangup(), add: guestMe || visitMe ? null : () => callUi.pick() }, calendar: { entered: (z) => calHook.c?.entered(z) } });
// ---- 小窗 (public/pip.js): Document Picture-in-Picture with my status, mic, camera, speaker, who's near / in the
// meeting and invites, for when Deck isn't in front
pip = initPip({ me: () => me, peers: () => peers, hearing: () => hearing, caster: () => caster, speaking, lookOf, buildSheet, otterImgs, agentName,
  statusOf, STATES, get dndHint() { return t('app.pip.dndHint'); }, poke: () => presence.poke(), toast, addSettingsSection,
  // the state I'm already in (by hand) keeps its note and its auto-restore time: picking it again changes nothing
  setStatus: (s) => { const cur = statusOf(me); if ((cur && !cur.auto ? cur.s : 'available') !== s) send({ t: 'status', s }); },
  tick: (dt) => { if (me) view.tick(dt); },
  mic: { muted: () => muted, ready: () => audioProvider.isAgora() ? audioProvider.microphoneReady() : !!local.mic?.getAudioTracks()[0], toggle: () => setMute(!muted) },
  share: { live: () => !!share.mid, stop: () => stopShare() }, speaker, meeting, members, duo, // duo: a 双人互动 request to me (pending, answer)
  // the minimap (附近的人): the map as it is now (the editor's rooms and walls included), and a person's card there with
  // what their card on the page offers (greet.js): 打招呼 (if I hear them), 走过去, 邀请 to my game, 邀请到身边, 请出去, 在 Raft 里私聊
  // 请出去 asks on the page (its confirm box): the page comes to the front first
  MAP, zoneAt, statusLine, card: {
    actions: (p) => [hearing.has(p.id) ? greet.forCard(p) : null, { icon: 'pin', label: t('app.card.walk'), title: t('app.card.walkTip'), go: () => walkTo(p) },
      invite.forCard(p), guestMe || visitMe ? null : meetinv.forCard(p), callUi.forCard(p), p.kind === 'agent' && !audioProvider.isAgora() ? frontFirst(secretary.forCard(p)) : null, p.kind === 'agent' ? frontFirst(agentDoor.forCard(p)) : null].filter(Boolean),
    profile: (p) => raftLinks.profile(p),
  } });
// ---- 房间 (public/rooms.js): on a phone, 「地图 | 房间」 in the top bar; 房间 lists this floor's rooms and who is in each
const rooms = initRooms({ MAP, me: () => me, peers: () => peers, zoneAt, speaking, lookOf, statusOf, STATES, meeting, pick, walkTo, toast,
  scene: () => scene, load: loadRooms, floor, blocked: () => mapEditor.active() });
// A key held while the tab went to the background never gets its keyup: let it go, or the 小窗's walking (pip.js
// steps the map while the page draws no frames) would keep the otter going.
document.addEventListener('visibilitychange', () => { if (document.hidden) mover.keys.clear(); });
// ---- 白板 (public/whiteboard.js): a 会议室's board (the meeting toolbar's 白板) and the boards on the open deck
const whiteboard = initWhiteboard({ send, me: () => me, MAP, zoneAt, mover, toast, blocked: () => meeting.full() || mapEditor.active(),
  stopWalking: () => { mover.keys.clear(); mover.unpush(); mover.clear(); } });
// ---- 邀请 in a 会议室 (public/meetinvite.js): pick people online to invite in; 邀请到身边 (a person's card, anywhere);
// the card when I'm invited
// ---- 参会链接 (public/meetlink.js): 复制参会链接 in 邀请 / 添加, and a link that brought me here
const meetLink = initMeetLink({ send, toast, floor, me: () => me });
const meetinv = initMeetInvite({ send, zoneAt, MAP, toast, members, me: () => me, peers: () => peers, room: () => meeting.room(), link: meetLink, guest: !!(guestMe || visitMe) });
// ---- 日历 (public/calendar.js): booked meetings, the 会议室 schedule, reminders with 去会议室 (members and agents; not guests)
const calendar = initCalendar({ floor, addSettingsSection, call: { ring: (ids) => callUi?.ring(ids), active: () => callUi?.active() ?? null }, me: () => me, peers: () => peers, toast, members, notes, room: () => meeting.room(), guest: !!guestMe || !!visitMe, dnd: () => statusOf(me)?.s === 'dnd',
  canLeave: () => !mapEditor.active() || confirm(t('app.map.leaveFloorDraft')) }); // (a visitor from another server: none)
calHook.c = calendar;
// ---- 语音转文字 (public/stt.js): my published mic to text, when someone needs it; captions are shown above. where: the
// room's id, not the zone object (zones are rebuilt on welcome, and a kept line comes back from localStorage)
// 热词 for speech to text: who I hear and who's in my room, the rooms' Raft channels, our product names
const HOT_PRODUCTS = ['Deck', 'Raft', 'Stamp', 'Holt', 'OtterBonk', 'DashScope', '水獭'];
const sttHotwords = () => {
  const z = me ? zoneAt(me.x, me.y)?.id : null, near = [...peers].filter(([id, p]) => hearing.has(id) || (z && zoneAt(p.x, p.y)?.id === z)).map(([, p]) => p.name);
  return [...(me ? [me.name] : []), ...near, ...HOT_PRODUCTS, ...raftLinks.names()];
};
const stt = initStt({ send, office: floor.room ?? 'main', me: () => me, hotwords: sttHotwords, where: () => (me ? { zone: zoneAt(me.x, me.y)?.id ?? null, x: me.x, y: me.y } : null), available: () => !audioProvider.isAgora() || audioProvider.sttAvailable(),
  captureIntent: (on) => { sttCaptureWanted = on; if (audioProvider.isAgora()) audioProvider.sttTap(on); },
  pipeline: () => voiceStopped ? null : audioProvider.isAgora() ? audioProvider.sttTap(sttCaptureWanted) : local.send, muted: () => muted, room: () => meeting.room(),
  addSettingsSection, toast, changed: () => { meeting.refresh(); if (onlineLast) showOnline(onlineLast); },
  partial: (text) => { if (text && me && meeting.room()) meeting.caption({ id: me.id, name: me.name, text, partial: true }, true); } });
// ---- 秘书 (public/secretary.js): 和 TA 说话 on an agent's card whose secretary is on; hold to talk, its voice in my audio
// graph at the agent's loudness; 对话中 / 在听 on name tags
const secretary = initSecretary({ room: floor.room ?? 'main', me: () => me, guest: () => !!(guestMe || visitMe), peers: () => peers, pipeline: () => (voiceStopped || audioProvider.isAgora() ? null : local.send),
  microphone: () => local.mic?.getAudioTracks()[0] ?? null,
  actx: () => actx, gain: (id) => { const p = peers.get(id); return p && !voiceStopped ? loudness(id, p) * speaker.gain() : 0; },
  hold: (on) => voice.hold(on), muted: () => muted,
  // interrupting it: its voice through the SFU (the agent's track) down to 10% here at once, before the server stops it
  duck: (id) => { const p = peers.get(id); if (!p) return; p.duckUntil = performance.now() + 1500; if (p.gainNode && actx) p.gainNode.gain.setTargetAtTime(0.1 * loudness(id, p) * speaker.gain(), actx.currentTime, 0.01); }, toast, changed: () => { if (onlineLast) showOnline(onlineLast); } });

// ---- 邀请访客 (public/guests.js): members only, from 设置 and the online list
const guestsUi = guestMe || visitMe ? null : initGuests({ room: 'main', toast, addSettingsSection });
// ---- 串门 (public/visits.js): 设置 → 串门 (where I can go, 回家, who may come here), the 「在 X 串门 · 回家」 pill
const visits = initVisits({ addSettingsSection, toast, visit: visitMe, guest: !!guestMe || !who.ok, others: () => myOthers, switchTo, leave: leaveHere, openSettings });
{
  const st = document.createElement('style');
  st.textContent = `#onlineList .gtag{font-size:11px;font-weight:600;background:#dbeafe;border:1px solid #1b1410;border-radius:999px;padding:0 6px;white-space:nowrap;align-self:center}
  #onlineList .vtag{background:#dcfce7;max-width:110px;overflow:hidden;text-overflow:ellipsis}
  #onlineList .ginv{display:flex;align-items:center;gap:6px;margin:4px 10px 2px;font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:2px 10px;background:#fff8e7;cursor:pointer}`;
  document.head.appendChild(st);
}
// ---- dressing up (public/dress.js): saved to your Raft account, shown to everyone on 保存
// Before boarding (the pre-join page) "me" is its stand-in and 保存 is queued until the welcome (prejoin.flush).
const dress = initDress({ me: () => me ?? prejoin?.standIn(), lookOf, send: (m) => (me ? send(m) : prejoin?.queue(m)), otterImgs, toast,
  persist: async (look, bodyExplicit) => {
    const r = await fetch(vq('/prejoin/' + encodeURIComponent(floor.room ?? 'main')), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ look, bodyExplicit }), signal: AbortSignal.timeout(10_000) });
    if (!r.ok) throw new Error('look save failed'); return r.json();
  }, saved: (l) => prejoin?.setLook(l) });
$('dress').onclick = dress.open;
dressOpen = dress.isOpen; dressReady(dress);
// ---- coach marks (public/tour.js, public/tours.js): the office's on the first visit, 设置 → 新手引导
const tours = initOfficeTour({ camera, me: () => me, MAP, addSettingsSection, send });
// 上船 pressed while the art was still loading (the join card's early handler): go now that it's in. Here, at the end,
// so start() finds everything above defined.
if (me) meetLink.welcome(); // a welcome that came before meetLink was set up (public/meetlink.js)
booted = true;
if (wantGo) start();
